// frontend/src/components/remediation/RemediationTaskModal.jsx
import React from "react";
import {
  buildControlLabel,
  cleanValue,
  formatAuditTransition,
  formatTimestamp,
  getEventDetail,
  getEventLabel,
  getEventToneColor,
  getInvestigationStatusLabel,
  getTaskStatusLabel,
  normalizeText,
} from "../../utils/complianceFormatters";
import {
  canShowReadyForValidationNextStep,
  getInvestigationMetadata,
  getInvestigationReadinessIssues,
  getValidationPendingGuidance,
  hasInvestigationContent,
  isClosedTask,
  taskNeedsCredentialInvestigation,
} from "../../utils/remediationTaskHelpers";

function MetaField({ label, value, wrap = false }) {
  return (
    <div className="open-item-pill" style={{ cursor: "default", alignItems: "flex-start" }}>
      <div className="open-item-label">{label}</div>
      <div
        className="open-item-value"
        style={{
          fontSize: 12,
          maxWidth: "100%",
          textAlign: "right",
          whiteSpace: wrap ? "normal" : "nowrap",
          overflow: wrap ? "visible" : "hidden",
          textOverflow: wrap ? "clip" : "ellipsis",
          wordBreak: wrap ? "break-word" : "normal",
          lineHeight: 1.35,
        }}
        title={String(value || "—")}
      >
        {value || "—"}
      </div>
    </div>
  );
}


function SectionJumpButton({ label, onClick }) {
  return (
    <button
      className="btn btn-ghost btn-xs"
      type="button"
      onClick={onClick}
      style={{
        color: "var(--text)",
        borderColor: "color-mix(in srgb, var(--text), transparent 72%)",
        background: "color-mix(in srgb, var(--text), transparent 94%)",
        boxShadow: "none",
      }}
    >
      {label}
    </button>
  );
}

function InvestigationTextArea({ label, value, onChange, placeholder, rows = 3, disabled = false }) {
  return (
    <label style={{ display: "grid", gap: 6, fontSize: 12, color: "var(--muted)", fontWeight: 800 }}>
      {label}
      <textarea
        value={value || ""}
        placeholder={placeholder}
        rows={rows}
        onChange={(event) => onChange(event.target.value)}
        disabled={disabled}
        style={{
          border: "1px solid color-mix(in srgb, var(--text), transparent 82%)",
          borderRadius: 12,
          background: "rgba(15, 23, 42, 0.72)",
          color: "var(--text)",
          padding: "10px 12px",
          resize: "vertical",
        }}
      />
    </label>
  );
}

function InvestigationInput({ label, value, onChange, placeholder, disabled = false }) {
  return (
    <label style={{ display: "grid", gap: 6, fontSize: 12, color: "var(--muted)", fontWeight: 800 }}>
      {label}
      <input
        type="text"
        value={value || ""}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
        disabled={disabled}
        style={{
          border: "1px solid color-mix(in srgb, var(--text), transparent 82%)",
          borderRadius: 12,
          background: "rgba(15, 23, 42, 0.72)",
          color: "var(--text)",
          padding: "10px 12px",
        }}
      />
    </label>
  );
}


function sectionShellStyle(tone = "default") {
  const color =
    tone === "danger"
      ? "var(--danger)"
      : tone === "warn"
      ? "var(--warn)"
      : tone === "ok"
      ? "var(--ok)"
      : tone === "accent"
      ? "var(--accent)"
      : "var(--text)";

  return {
    border: `1px solid color-mix(in srgb, ${color}, transparent 78%)`,
    borderRadius: 16,
    padding: 14,
    display: "grid",
    gap: 10,
    background:
      tone === "default"
        ? "transparent"
        : `color-mix(in srgb, ${color}, transparent 94%)`,
  };
}

function statusPillStyle(tone = "default") {
  const color =
    tone === "danger"
      ? "var(--danger)"
      : tone === "warn"
      ? "var(--warn)"
      : tone === "ok"
      ? "var(--ok)"
      : tone === "accent"
      ? "var(--accent)"
      : "var(--muted)";

  return {
    display: "inline-flex",
    alignItems: "center",
    borderRadius: 999,
    padding: "4px 8px",
    fontSize: 11,
    fontWeight: 900,
    lineHeight: 1.1,
    color,
    border: `1px solid color-mix(in srgb, ${color}, transparent 62%)`,
    background: `color-mix(in srgb, ${color}, transparent 90%)`,
    whiteSpace: "nowrap",
  };
}

function modalSectionHeader({ eyebrow, title, description, action }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: 10,
        flexWrap: "wrap",
      }}
    >
      <div>
        <div
          style={{
            fontSize: 12,
            fontWeight: 900,
            textTransform: "uppercase",
            letterSpacing: ".06em",
            color: "var(--muted)",
          }}
        >
          {eyebrow}
        </div>
        <div style={{ fontWeight: 950, fontSize: 15, marginTop: 2 }}>{title}</div>
        {description ? (
          <div style={{ color: "var(--muted)", fontSize: 13, lineHeight: 1.45, marginTop: 4 }}>
            {description}
          </div>
        ) : null}
      </div>
      {action || null}
    </div>
  );
}

function getTaskMetadata(task = {}) {
  return task.task_metadata || task.metadata || {};
}

function getLastValidation(task = {}) {
  const metadata = getTaskMetadata(task);
  return metadata.last_validation || metadata.lastValidation || null;
}

function asObject(value) {
  return value && typeof value === "object" && !Array.isArray(value) ? value : {};
}

function getWorkflowMetadata(task = {}) {
  const metadata = getTaskMetadata(task);
  return asObject(metadata.workflow || metadata.workflow_metadata || metadata.workflowMetadata);
}

function getWorkflowTaskState(task = {}) {
  const metadata = getTaskMetadata(task);
  return asObject(metadata.workflow_task_state || metadata.workflowTaskState);
}

function getWorkflowValidationSync(task = {}) {
  const metadata = getTaskMetadata(task);
  return asObject(metadata.workflow_validation || metadata.workflowValidation);
}

function getWorkflowRecoveryMetadata(task = {}) {
  const metadata = getTaskMetadata(task);
  return asObject(metadata.workflow_recovery || metadata.workflowRecovery);
}

function parseWorkflowTimestamp(value) {
  const cleaned = cleanValue(value, "");
  if (!cleaned) return null;

  const parsed = new Date(cleaned);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function getWorkflowRecoveryTimestamp(task = {}) {
  const workflow = getWorkflowMetadata(task);
  const workflowRecovery = getWorkflowRecoveryMetadata(task);

  return parseWorkflowTimestamp(
    workflowRecovery.recovered_at ||
      workflowRecovery.recoveredAt ||
      workflow.last_recovered_at ||
      workflow.lastRecoveredAt
  );
}

function getWorkflowValidationSyncTimestamp(task = {}) {
  const validationSync = getWorkflowValidationSync(task);
  return parseWorkflowTimestamp(validationSync.synced_at || validationSync.syncedAt);
}

function isWorkflowValidationFailureCurrent(task = {}) {
  const validationSync = getWorkflowValidationSync(task);
  const validationResult = normalizeText(
    validationSync.validation_result ||
      validationSync.validationResult ||
      validationSync.result ||
      ""
  );

  if (validationResult !== "failed") return false;

  const recoveredAt = getWorkflowRecoveryTimestamp(task);
  if (!recoveredAt) return true;

  const validationSyncedAt = getWorkflowValidationSyncTimestamp(task);
  if (!validationSyncedAt) return false;

  return validationSyncedAt.getTime() > recoveredAt.getTime();
}

function isWorkflowRecoverableTask(task = {}) {
  if (isClosedTask(task)) return false;

  const taskStatus = normalizeText(task.status || task.task_status || "");
  const validationStatus = normalizeText(task.validation_status || task.validationStatus || "");

  if (["validated", "closed", "cancelled"].includes(taskStatus)) return false;

  const workflow = getWorkflowMetadata(task);
  const workflowStatus = normalizeText(
    workflow.status || workflow.workflow_status || workflow.workflowStatus || ""
  );
  const stepStatus = normalizeText(
    workflow.current_step_status || workflow.currentStepStatus || ""
  );
  const currentValidationFailed = isWorkflowValidationFailureCurrent(task);

  return (
    validationStatus === "failed" ||
    taskStatus === "ready_for_validation" ||
    workflowStatus === "failed" ||
    stepStatus === "failed" ||
    currentValidationFailed
  );
}

function getNestedSyncStep(sync = {}) {
  return asObject(sync.current_step || sync.currentStep || sync.workflow_current_step || sync.workflowCurrentStep);
}

function getNestedSyncTransition(sync = {}) {
  return asObject(sync.transition || sync.workflow_transition || sync.workflowTransition);
}

function getWorkflowIdFromTask(task = {}) {
  const workflow = getWorkflowMetadata(task);
  const taskState = getWorkflowTaskState(task);
  const validation = getWorkflowValidationSync(task);

  return (
    workflow.workflow_id ||
    workflow.workflowId ||
    taskState.workflow_id ||
    taskState.workflowId ||
    validation.workflow_id ||
    validation.workflowId ||
    ""
  );
}

function workflowStatusTone(value = "") {
  const normalized = normalizeText(value);
  if (["failed", "blocked", "cancelled"].includes(normalized)) return "danger";
  if (["awaiting_validation", "awaiting_approval", "waiting", "queued"].includes(normalized)) return "warn";
  if (["completed", "validated", "closed", "passed", "satisfied", "approved"].includes(normalized)) return "ok";
  if (["executing", "in_progress", "assigned", "advanced"].includes(normalized)) return "accent";
  return "default";
}

function formatWorkflowLabel(value = "") {
  const normalized = normalizeText(value);
  if (!normalized) return "—";
  if (normalized === "awaiting_validation") return "Awaiting Validation";
  if (normalized === "awaiting_approval") return "Awaiting Approval";
  if (normalized === "in_progress") return "In Progress";
  if (normalized === "ready_for_validation") return "Ready For Validation";
  return formatTitleCase(normalized);
}

function formatWorkflowSentence(value = "") {
  const raw = cleanValue(value, "");
  if (!raw) return "Workflow sync recorded.";

  const transitionMatch = raw.match(
    /(?:illegal\s+)?workflow\s+step\s+transition[:\s]+['"]?([^'"]+?)['"]?\s*->\s*['"]?([^'".]+)['"]?/i
  );

  if (transitionMatch) {
    const fromState = formatWorkflowLabel(transitionMatch[1]);
    const toState = formatWorkflowLabel(transitionMatch[2]);
    return `The workflow could not move from ${fromState} to ${toState}. Review the current workflow state before retrying.`;
  }

  return raw
    .replace(/awaiting_validation/g, "Awaiting Validation")
    .replace(/awaiting_approval/g, "Awaiting Approval")
    .replace(/ready_for_validation/g, "Ready For Validation")
    .replace(/in_progress/g, "In Progress")
    .replace(/\bfailed\b/g, "Failed")
    .replace(/\bcompleted\b/g, "Completed")
    .replace(/\bexecuting\b/g, "Executing")
    .replace(/\bqueued\b/g, "Queued");
}

function WorkflowSyncSummary({ title, sync, historical = false }) {
  const payload = asObject(sync);
  const transition = getNestedSyncTransition(payload);
  const currentStep = getNestedSyncStep(payload);
  const action = cleanValue(payload.action, "logged");
  const message = formatWorkflowSentence(payload.message || payload.reason || "Workflow sync recorded.");
  const workflowStatus =
    transition.workflow_status ||
    transition.workflowStatus ||
    payload.target_workflow_status ||
    payload.targetWorkflowStatus ||
    currentStep.workflow_status ||
    currentStep.workflowStatus ||
    "";
  const stepStatus = transition.current_step_status || transition.currentStepStatus || currentStep.step_status || currentStep.stepStatus || "";
  const stepName = currentStep.step_name || currentStep.stepName || currentStep.step_key || currentStep.stepKey || "—";
  const targetStatus = payload.target_workflow_status || payload.targetWorkflowStatus || "";
  const result = payload.validation_result || payload.validationResult || "";
  const summaryTone = historical ? "default" : workflowStatusTone(result || workflowStatus || action);
  const actionTone = workflowStatusTone(result || workflowStatus || action);
  const actionLabel = result ? formatValidationLabel(result) : workflowStatus ? formatWorkflowLabel(workflowStatus) : formatWorkflowLabel(action);
  const syncedAt = payload.synced_at || payload.syncedAt;

  return (
    <div style={sectionShellStyle(summaryTone)}>
      {modalSectionHeader({
        eyebrow: historical ? `Previous ${title}` : title,
        title: historical ? `Previous: ${message}` : message,
        description: historical
          ? `Historical sync captured before the latest workflow recovery.${syncedAt ? ` Synced ${formatTimestamp(syncedAt)}` : ""}`
          : syncedAt
          ? `Synced ${formatTimestamp(syncedAt)}`
          : "Workflow sync is recorded on this task.",
        action: (
          <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap", justifyContent: "flex-end" }}>
            {historical ? <span style={statusPillStyle("default")}>Historical</span> : null}
            <span style={statusPillStyle(actionTone)}>{actionLabel}</span>
          </div>
        ),
      })}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 8 }}>
        {workflowStatus ? <MetaField label="Workflow" value={formatWorkflowLabel(workflowStatus)} wrap /> : null}
        {targetStatus ? <MetaField label="Target" value={formatWorkflowLabel(targetStatus)} wrap /> : null}
        {stepStatus ? <MetaField label="Step Status" value={formatWorkflowLabel(stepStatus)} wrap /> : null}
        {stepName ? <MetaField label="Current Step" value={formatWorkflowLabel(stepName)} wrap /> : null}
        {result ? <MetaField label="Result" value={formatValidationLabel(result)} wrap /> : null}
      </div>
    </div>
  );
}

function WorkflowOrchestrationPanel({ task, onOpenWorkflowCenter, onRecoverWorkflow, recovering = false, recoveryError = "" }) {
  const workflow = getWorkflowMetadata(task);
  const taskStateSync = getWorkflowTaskState(task);
  const validationSync = getWorkflowValidationSync(task);
  const taskStateTransition = getNestedSyncTransition(taskStateSync);
  const validationTransition = getNestedSyncTransition(validationSync);
  const workflowRecovery = getWorkflowRecoveryMetadata(task);
  const recoveryRetry = asObject(workflowRecovery.retry);
  const workflowId = getWorkflowIdFromTask(task);
  const templateSlug = workflow.template_slug || workflow.templateSlug || "—";
  const hasRecovery = Object.keys(workflowRecovery).length > 0;
  const recoverable = isWorkflowRecoverableTask(task);
  const latestWorkflowStatus = hasRecovery
    ? recoveryRetry.workflow_status ||
      recoveryRetry.workflowStatus ||
      workflow.workflow_status ||
      workflow.workflowStatus ||
      workflow.status ||
      taskStateTransition.workflow_status ||
      taskStateTransition.workflowStatus ||
      "queued"
    : validationTransition.workflow_status ||
      validationTransition.workflowStatus ||
      taskStateTransition.workflow_status ||
      taskStateTransition.workflowStatus ||
      workflow.status ||
      workflow.workflow_status ||
      workflow.workflowStatus ||
      "linked";
  const hasTaskStateSync = Object.keys(taskStateSync).length > 0;
  const hasValidationSync = Object.keys(validationSync).length > 0;
  const hasWorkflow = Boolean(workflowId || Object.keys(workflow).length || hasTaskStateSync || hasValidationSync || hasRecovery);

  if (!hasWorkflow) return null;

  const handleOpen = () => {
    if (typeof onOpenWorkflowCenter !== "function") return;
    onOpenWorkflowCenter({
      workflowId,
      workflow_id: workflowId,
      task,
      workflow,
    });
  };

  const handleRecover = () => {
    if (!recoverable || recovering || typeof onRecoverWorkflow !== "function") return;
    onRecoverWorkflow(task);
  };

  return (
    <div style={sectionShellStyle("accent")}>
      {modalSectionHeader({
        eyebrow: "Workflow Orchestration",
        title: "Linked Workflow",
        description: workflowId
          ? "This remediation task is linked to an orchestration workflow. Task updates and validation results can drive workflow state."
          : "Workflow sync metadata is available for this task.",
        action: (
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", justifyContent: "flex-end" }}>
            <span style={statusPillStyle(workflowStatusTone(latestWorkflowStatus))}>{formatWorkflowLabel(latestWorkflowStatus)}</span>
            {recoverable && typeof onRecoverWorkflow === "function" ? (
              <button
                className="btn btn-primary btn-xs"
                type="button"
                onClick={handleRecover}
                disabled={recovering}
              >
                {recovering ? "Restoring…" : "Restore Workflow"}
              </button>
            ) : null}
            {workflowId && typeof onOpenWorkflowCenter === "function" ? (
              <button className="btn btn-ghost btn-xs" type="button" onClick={handleOpen} disabled={recovering}>
                Open Workflow Center
              </button>
            ) : null}
          </div>
        ),
      })}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 8 }}>
        <MetaField label="Workflow ID" value={workflowId || "—"} wrap />
        <MetaField label="Template" value={templateSlug} wrap />
        <MetaField label="Workflow Status" value={formatWorkflowLabel(latestWorkflowStatus)} />
        <MetaField label="Linked Table" value={workflow.linked_table || workflow.linkedTable || "remediation_tasks"} />
      </div>

      {recoveryError ? (
        <div style={{ color: "var(--danger)", fontSize: 12, lineHeight: 1.45, fontWeight: 800 }}>
          {recoveryError}
        </div>
      ) : null}

      {hasRecovery ? (
        <div style={sectionShellStyle("ok")}>
          {modalSectionHeader({
            eyebrow: "Workflow Recovery",
            title: "Workflow recovered to active work.",
            description: workflowRecovery.recovered_at || workflowRecovery.recoveredAt
              ? `Recovered ${formatTimestamp(workflowRecovery.recovered_at || workflowRecovery.recoveredAt)}`
              : "Recovery was recorded on this task.",
            action: <span style={statusPillStyle("ok")}>Recovered</span>,
          })}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 8 }}>
            <MetaField label="Previous Task" value={formatWorkflowLabel(workflowRecovery.previous_task_status || workflowRecovery.previousTaskStatus)} />
            <MetaField label="Current Task" value={formatWorkflowLabel(workflowRecovery.next_task_status || workflowRecovery.nextTaskStatus)} />
            <MetaField label="Previous Validation" value={formatValidationLabel(workflowRecovery.previous_validation_status || workflowRecovery.previousValidationStatus)} />
            <MetaField label="Current Validation" value={formatValidationLabel(workflowRecovery.next_validation_status || workflowRecovery.nextValidationStatus)} />
          </div>
        </div>
      ) : null}

      {hasTaskStateSync || hasValidationSync ? (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 10 }}>
          {hasTaskStateSync ? <WorkflowSyncSummary title="Task-State Sync" sync={taskStateSync} historical={hasRecovery} /> : null}
          {hasValidationSync ? <WorkflowSyncSummary title="Validation Sync" sync={validationSync} historical={hasRecovery} /> : null}
        </div>
      ) : (
        <div style={{ color: "var(--muted)", fontSize: 12, lineHeight: 1.45 }}>
          No task-state or validation workflow sync has been recorded yet. Updates will appear here after task status changes or validation runs.
        </div>
      )}
    </div>
  );
}

function normalizeListPayload(payload, primaryKey) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.[primaryKey])) return payload[primaryKey];
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.results)) return payload.results;
  return [];
}

function getValidationRows(task, validationHistory) {
  const rows = normalizeListPayload(validationHistory, "validation_runs");
  if (rows.length > 0) return rows;

  const lastValidation = getLastValidation(task);
  if (!lastValidation) return [];

  return [
    {
      id: lastValidation.validation_run_id || "last-validation",
      validation_status: lastValidation.status,
      validation_result: lastValidation.result,
      validation_reason: lastValidation.reason,
      failure_reason: lastValidation.failure_reason,
      evidence_ref: lastValidation.evidence_ref,
      evidence_sha256: lastValidation.evidence_sha256,
      evidence_id: lastValidation.evidence_id,
      validator_type: lastValidation.validator_type,
      validator_name: lastValidation.validator_name,
      executed_at: lastValidation.executed_at,
      executed_by: lastValidation.executed_by,
      executed_by_name: lastValidation.executed_by_name,
      metadata: lastValidation.metadata,
    },
  ];
}

function getEvidenceRows(evidencePayload) {
  return normalizeListPayload(evidencePayload, "evidence");
}

function validationTone(value) {
  const normalized = normalizeText(value);
  if (normalized === "failed") return "danger";
  if (normalized === "passed") return "ok";
  if (normalized === "approved_exception") return "ok";
  if (normalized === "manual_override") return "warn";
  if (normalized === "stale") return "warn";
  return "default";
}

function formatTitleCase(value = "") {
  const cleaned = cleanValue(value, "").replace(/_/g, " ").trim();
  if (!cleaned) return "";

  return cleaned
    .split(/\s+/)
    .map((word) => {
      if (/^[A-Z0-9]{2,}$/.test(word)) return word;
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(" ");
}

function formatValidationLabel(value) {
  const normalized = normalizeText(value || "pending");
  if (normalized === "approved_exception") return "Approved Exception";
  if (normalized === "manual_override") return "Manual Override";
  if (normalized === "ready_for_validation") return "Ready For Validation";
  if (normalized === "passed" || normalized === "pass") return "Passed";
  if (normalized === "failed" || normalized === "fail") return "Failed";
  if (normalized === "stale") return "Stale";
  if (normalized === "pending") return "Pending";
  return formatTitleCase(value || "pending") || "Pending";
}

function isApprovedExceptionTask(task = {}) {
  const validationStatus = normalizeText(task.validation_status || task.validationStatus || "");
  const lastValidation = getLastValidation(task) || {};
  const lastStatus = normalizeText(
    lastValidation.status ||
      lastValidation.validation_status ||
      lastValidation.result ||
      lastValidation.validation_result ||
      ""
  );
  const metadata = lastValidation.metadata || {};
  const governance = normalizeText(
    metadata.validation_governance || metadata.governance || metadata.exception_type || ""
  );
  const decisionPath = normalizeText(metadata.decision_path || "");

  return (
    validationStatus === "approved_exception" ||
    lastStatus === "approved_exception" ||
    governance === "approved_exception" ||
    decisionPath.includes("approved_exception") ||
    decisionPath.includes("retain_as_approved_exception")
  );
}

function formatDecisionPath(value = "") {
  const cleaned = cleanValue(value, "");
  if (!cleaned) return "Approved Exception";

  return formatTitleCase(
    cleaned
      .replace(/^path_[a-z]_/i, "")
      .replace(/retain_as_approved_exception/gi, "retain as approved exception")
      .replace(/approved_controlled_trust/gi, "approved controlled trust")
      .replace(/accept_as_approved_access/gi, "accept as approved access")
      .replace(/temporary_approved_exception/gi, "temporary approved exception")
  );
}

function formatAuditDisplayText(value = "") {
  const raw = cleanValue(value, "");
  if (!raw) return "";

  return raw
    .replace(/\bapproved_exception\b/g, "Approved Exception")
    .replace(/\bmanual_override\b/g, "Manual Override")
    .replace(/\bready_for_validation\b/g, "Ready For Validation")
    .replace(/\bin_progress\b/g, "In Progress")
    .replace(/\bnot_started\b/g, "Not Started")
    .replace(/\bvalidated\b/g, "Validated")
    .replace(/\bclosed\b/g, "Closed")
    .replace(/\bblocked\b/g, "Blocked")
    .replace(/\bopen\b/g, "Open")
    .replace(/\bpassed\b/g, "Passed")
    .replace(/\bfailed\b/g, "Failed")
    .replace(/\bpending\b/g, "Pending");
}

function getValidationRunDetail(run = {}, status = "") {
  const normalizedStatus = normalizeText(status);
  const reason =
    run.failure_reason ||
    run.validation_reason ||
    run.reason ||
    run.validation_result ||
    "";

  if (normalizedStatus === "approved_exception") {
    const metadata = run.metadata || {};
    const reviewDate = cleanValue(metadata.review_date, "");
    const owner = cleanValue(metadata.business_owner || metadata.decision_owner || metadata.approver_name, "");
    const decisionPath = formatDecisionPath(metadata.decision_path || "");

    return [
      "Approved Exception recorded.",
      owner ? `Decision owner: ${owner}.` : "",
      reviewDate ? `Review date: ${reviewDate}.` : "",
      decisionPath ? `Decision path: ${decisionPath}.` : "",
    ]
      .filter(Boolean)
      .join(" ");
  }

  return formatAuditDisplayText(reason || "No validation detail provided.");
}

function getValidationActorLabel(run = {}) {
  const metadata = run.metadata || {};
  const candidates = [
    metadata.business_owner,
    metadata.decision_owner,
    metadata.approver_name,
    metadata.approved_by_name,
    run.executed_by_name,
    run.executed_by,
    run.validator_name,
  ];

  for (const candidate of candidates) {
    const value = cleanValue(candidate, "");
    const normalized = normalizeText(value);

    if (!value) continue;
    if (["approved_exception", "approved exception", "exception", "validation", "validator"].includes(normalized)) {
      continue;
    }

    return value;
  }

  return "System";
}

function getEvidenceMetadata(item = {}) {
  return item.metadata || item.evidence_metadata || item.evidenceMetadata || {};
}

function getEvidenceTrustProfile(item = {}) {
  const metadata = getEvidenceMetadata(item);
  const evidenceType = normalizeText(item.evidence_type || item.type || "");
  const storagePath = String(item.storage_path || item.storagePath || "").trim();
  const source = normalizeText(metadata.source || metadata.evidence_source || item.source || item.evidence_source || "");
  const verificationStatus = normalizeText(metadata.verification_status || item.verification_status || "");
  const hasSha = Boolean(cleanValue(item.sha256 || item.sha_256 || metadata.sha256, ""));

  if (
    verificationStatus === "verified" ||
    source.includes("aws") ||
    source.includes("connector") ||
    source.includes("system_collected") ||
    storagePath.startsWith("aws://") ||
    storagePath.startsWith("archive://")
  ) {
    return {
      tone: "ok",
      label: "Verified Evidence",
      detail: hasSha
        ? "System or connector evidence with integrity hash captured."
        : "System or connector evidence captured from a source workflow.",
    };
  }

  if (hasSha) {
    return {
      tone: "accent",
      label: "Has Integrity Hash",
      detail: "User-attached evidence includes a SHA-256 hash for integrity review.",
    };
  }

  if (
    evidenceType === "note" ||
    evidenceType === "file_reference" ||
    evidenceType === "storage_path" ||
    storagePath.startsWith("manual://") ||
    source.includes("remediation_task_modal") ||
    source.includes("manual") ||
    !storagePath
  ) {
    return {
      tone: "warn",
      label: "Manual Evidence · User Provided",
      detail: "User-attached reference. Not independently verified by OASYS until validated against source evidence or approved by manual override.",
    };
  }

  return {
    tone: "default",
    label: "Evidence Reference",
    detail: "Evidence reference attached to this remediation task.",
  };
}

function getClosureWorkflowSteps(task = {}, evidenceRows = []) {
  const status = normalizeText(task.status);
  const validationStatus = normalizeText(task.validation_status);
  const evidenceAttached = evidenceRows.length > 0;
  const taskValidated = status === "validated" || status === "closed";
  const sourceValidationPassed = validationStatus === "passed";
  const manualOverrideAccepted = validationStatus === "manual_override";
  const approvedExceptionAccepted = isApprovedExceptionTask(task);
  const validationAccepted =
    taskValidated || sourceValidationPassed || manualOverrideAccepted || approvedExceptionAccepted;
  const validationFailed = validationStatus === "failed";
  const taskClosed = status === "closed";
  const statusNeedsFinalization = validationAccepted && !taskValidated;

  return [
    {
      key: "evidence",
      label: "Evidence attached",
      complete: evidenceAttached,
      tone: evidenceAttached ? "ok" : "warn",
      detail: evidenceAttached
        ? `${evidenceRows.length} evidence item${evidenceRows.length === 1 ? "" : "s"} linked to this task.`
        : "Attach supporting evidence or a reference before closure.",
    },
    {
      key: "validation",
      label: approvedExceptionAccepted
        ? "Approved exception recorded"
        : validationFailed
        ? "Validation failed"
        : manualOverrideAccepted
        ? "Manual override recorded"
        : sourceValidationPassed || taskValidated
        ? "Validation passed"
        : "Run validation",
      complete: validationAccepted,
      tone: validationAccepted
        ? approvedExceptionAccepted || sourceValidationPassed || taskValidated
          ? "ok"
          : "warn"
        : validationFailed
        ? "danger"
        : "accent",
      detail: validationAccepted
        ? approvedExceptionAccepted
          ? "Approved exception is recorded. Finalize the task status before closure."
          : manualOverrideAccepted
          ? "Manual override is recorded. Finalize the task status before closure."
          : "Source-system validation passed. Finalize the task status before closure."
        : validationFailed
        ? "Validation failed. Complete another remediation pass, then run validation again."
        : "Run validation after the remediation and evidence are ready.",
    },
    {
      key: "closure",
      label: taskClosed ? "Task closed" : statusNeedsFinalization ? "Mark validated" : taskValidated ? "Close task" : "Close task",
      complete: taskClosed,
      tone: taskClosed ? "ok" : statusNeedsFinalization ? "warn" : taskValidated ? "accent" : "default",
      detail: taskClosed
        ? "Closure is recorded in the task audit trail."
        : statusNeedsFinalization
        ? approvedExceptionAccepted
          ? "Add a closure reason and use Validate & Close to finalize this accepted-risk task."
          : "Set Status to Validated and save the task. Then close it with a closure reason."
        : taskValidated
        ? "Set Status to Closed, add a closure reason, and save the task."
        : "Closure is blocked until validation is accepted and task status is finalized.",
    },
  ];
}

function getClosureWorkflowGuidance(task = {}, evidenceRows = []) {
  const status = normalizeText(task.status);
  const validationStatus = normalizeText(task.validation_status);
  const evidenceAttached = evidenceRows.length > 0;
  const taskValidated = status === "validated" || status === "closed";
  const sourceValidationPassed = validationStatus === "passed";
  const manualOverrideAccepted = validationStatus === "manual_override";
  const approvedExceptionAccepted = isApprovedExceptionTask(task);
  const validationAccepted =
    taskValidated || sourceValidationPassed || manualOverrideAccepted || approvedExceptionAccepted;

  if (status === "closed") {
    return approvedExceptionAccepted
      ? "Evidence attached → Approved Exception recorded → task closed → audit trail confirms accepted-risk closure."
      : "Evidence attached → validation accepted → task closed → audit trail confirms closure.";
  }

  if (!evidenceAttached) {
    return approvedExceptionAccepted
      ? "Approved Exception is recorded. Attach supporting evidence where possible, then mark the task Validated and close it with a closure reason."
      : "Attach evidence first, then run validation. Manual evidence is documentation, not proof by itself.";
  }

  if (approvedExceptionAccepted) {
    return taskValidated
      ? "Evidence attached → Approved Exception recorded → task validated → close task with a closure reason."
      : "Evidence attached → Approved Exception recorded → add closure reason → Validate & Close.";
  }

  if (validationStatus === "failed") {
    return "Evidence is attached, but validation failed. Complete the source-system fix, then run validation again.";
  }

  if (!validationAccepted) {
    return "Evidence is attached. Next step: run validation or Full Cloud Assessment to confirm the source condition is remediated.";
  }

  if (!taskValidated) {
    return manualOverrideAccepted
      ? "Evidence attached → manual override accepted → set task status to Validated → then close with a closure reason."
      : "Evidence attached → validation passed → set task status to Validated → then close with a closure reason.";
  }

  return "Evidence attached → task validated → close task → audit trail confirms closure.";
}

function ClosureWorkflowSteps({ task, evidenceRows }) {
  const steps = getClosureWorkflowSteps(task, evidenceRows);

  return (
    <div style={{ display: "grid", gap: 10 }}>
      <div
        style={{
          border: "1px solid color-mix(in srgb, var(--accent), transparent 72%)",
          borderRadius: 14,
          padding: "10px 12px",
          background: "color-mix(in srgb, var(--accent), transparent 94%)",
          color: "var(--text)",
          fontSize: 12,
          lineHeight: 1.45,
        }}
      >
        <strong>Closure workflow:</strong> {getClosureWorkflowGuidance(task, evidenceRows)}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 8 }}>
        {steps.map((step, index) => (
          <div
            key={step.key}
            className="open-item-pill"
            style={{
              cursor: "default",
              alignItems: "stretch",
              display: "grid",
              gap: 6,
              borderColor:
                step.tone === "ok"
                  ? "color-mix(in srgb, var(--ok), transparent 70%)"
                  : step.tone === "danger"
                  ? "color-mix(in srgb, var(--danger), transparent 70%)"
                  : step.tone === "warn"
                  ? "color-mix(in srgb, var(--warn), transparent 70%)"
                  : step.tone === "accent"
                  ? "color-mix(in srgb, var(--accent), transparent 70%)"
                  : "color-mix(in srgb, var(--text), transparent 86%)",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center" }}>
              <span style={{ fontSize: 11, color: "var(--muted)", fontWeight: 900 }}>Step {index + 1}</span>
              <span style={statusPillStyle(step.tone)}>{step.complete ? "Done" : "Next"}</span>
            </div>
            <div style={{ fontSize: 13, fontWeight: 950 }}>{step.label}</div>
            <div style={{ color: "var(--muted)", fontSize: 12, lineHeight: 1.4 }}>{step.detail}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function closureReadiness(task = {}, evidenceRows = []) {
  const status = normalizeText(task.status);
  const validationStatus = normalizeText(task.validation_status);
  const hasValidatedStatus = status === "validated" || status === "closed";
  const sourceValidationPassed = validationStatus === "passed";
  const manualOverrideAccepted = validationStatus === "manual_override";
  const approvedExceptionAccepted = isApprovedExceptionTask(task);
  const hasPassingValidation = sourceValidationPassed || manualOverrideAccepted || approvedExceptionAccepted;
  const hasEvidence = evidenceRows.length > 0;

  if (status === "closed") {
    return {
      tone: "ok",
      label: "Closed",
      description: approvedExceptionAccepted
        ? "This remediation task is closed with an approved exception preserved in the audit trail. The source finding may still exist by design, but the retained risk is documented, approved, evidence-backed, and time-bound."
        : "This remediation task is closed and locked to preserve the audit trail.",
    };
  }

  if (hasValidatedStatus && approvedExceptionAccepted) {
    return {
      tone: "ok",
      label: "Approved Exception Recorded — Ready to Close",
      description: hasEvidence
        ? "This task is Validated through an approved, evidence-backed exception. Add a closure reason and close the task."
        : "This task is Validated through an approved exception. Add linked evidence where possible, then close the task with a closure reason.",
    };
  }

  if (hasValidatedStatus && hasPassingValidation) {
    return {
      tone: "ok",
      label: "Ready to Close",
      description: hasEvidence
        ? "This task status is validated and supporting evidence is available for audit review. Add a closure reason and close the task."
        : "This task status is validated. Add linked evidence where possible before closure for a stronger audit record.",
    };
  }

  if (approvedExceptionAccepted && !hasValidatedStatus) {
    return {
      tone: "ok",
      label: "Approved Exception Recorded — Ready to Close",
      description:
        "This finding was retained through an approved, evidence-backed exception. The source finding may still exist by design. Add a closure reason, then use Validate & Close to finalize the audit trail in one step.",
    };
  }

  if (hasPassingValidation && !hasValidatedStatus) {
    return {
      tone: manualOverrideAccepted ? "warn" : "accent",
      label: manualOverrideAccepted
        ? "Validation Accepted — Status Not Finalized"
        : "Validation Passed — Status Not Finalized",
      description: manualOverrideAccepted
        ? "Manual override is recorded and evidence is attached, but the task status is still not Validated. Set Status to Validated, save the task, then close it with a closure reason."
        : "Validation passed and evidence is attached, but the task status is still not Validated. Set Status to Validated, save the task, then close it with a closure reason.",
    };
  }

  if (status === "ready_for_validation") {
    return {
      tone: "accent",
      label: "Ready for Validation",
      description: "Run validation before closing this task. Closure is blocked until the task reaches validated status.",
    };
  }

  if (validationStatus === "failed") {
    return {
      tone: "danger",
      label: "Validation Failed",
      description: "This task needs another remediation pass before it can be validated and closed.",
    };
  }

  return {
    tone: "warn",
    label: "Not Ready to Close",
    description: "Follow the required workflow before closure: attach evidence, complete remediation, pass validation, then close with a reason.",
  };
}


function getClosureGuardTone(guard = {}) {
  const status = normalizeText(guard.closure_status || guard.status || "");
  if (guard.closure_allowed || status === "allowed" || status === "ready") return "ok";
  if (status === "blocked" || status === "failed") return "danger";
  if (status === "review_required" || status === "needs_review") return "warn";
  return "warn";
}

function getClosureGuardLabel(guard = {}) {
  const status = normalizeText(guard.closure_status || "");
  if (guard.closure_allowed || status === "allowed" || status === "ready") return "Ready";
  if (status === "blocked") return "Blocked";
  if (status === "review_required") return "Review Required";
  if (guard.task_exists === false) return "Unavailable";
  return "No Validation Attached";
}

function getLatestValidationLabel(guard = {}) {
  const latest = asObject(guard.latest_validation);
  const pieces = [latest.framework, latest.control_id, latest.rule_id, latest.resource_id]
    .map((item) => cleanValue(item, ""))
    .filter(Boolean);
  return pieces.length ? pieces.join(" / ") : "Latest validation";
}

function getClosureGuardPrimaryReason(guard = {}) {
  const latest = asObject(guard.latest_validation);
  const reasons = Array.isArray(guard.blocking_reasons) ? guard.blocking_reasons : [];
  return cleanValue(latest.reason, "") || cleanValue(reasons[0], "") || cleanValue(guard.summary, "No closure guard detail available.");
}

function getRunValidationButtonLabel(task = {}) {
  const status = normalizeText(task.status || task.task_status || "");
  return status === "ready_for_validation" ? "Run Validation" : "Mark Ready & Run Validation";
}


function formatGuardVersionLabel(value = "") {
  const raw = cleanValue(value, "");
  if (!raw) return "—";

  const match = raw.match(/(?:^|\.|_)v(\d+)$/i) || raw.match(/v(\d+)/i);
  if (match?.[1]) return `V${match[1]}`;

  return raw;
}

function formatFrontendCopy(value = "") {
  const raw = cleanValue(value, "");
  if (!raw) return "";

  return raw
    .replace(/\bevidence_id\b/g, "evidence ID")
    .replace(/\bvalidation_result_id\b/g, "validation result ID")
    .replace(/\bvalidation_run_id\b/g, "validation run ID")
    .replace(/\blink_id\b/g, "link ID")
    .replace(/\btask_id\b/g, "task ID");
}


function ValidationClosureGuardPanel({
  task,
  closureGuard,
  loading = false,
  error = "",
  onRefresh,
  onRunValidation,
  onOpenValidationDashboard,
  validationActionLoading = false,
  validationActionError = "",
}) {
  const guard = closureGuard && typeof closureGuard === "object" ? closureGuard : null;
  const tone = guard ? getClosureGuardTone(guard) : error ? "danger" : "warn";
  const label = guard ? getClosureGuardLabel(guard) : loading ? "Loading" : "No Validation Attached";
  const latestValidation = asObject(guard?.latest_validation);
  const evidenceLinks = Array.isArray(guard?.evidence_links) ? guard.evidence_links : [];
  const primaryEvidence = evidenceLinks[0] || null;
  const confidenceScore = cleanValue(guard?.confidence_score, "");
  const confidenceLevel = cleanValue(guard?.confidence_level, "");
  const reason = guard ? getClosureGuardPrimaryReason(guard) : "Loading deterministic closure guard status.";
  const recommendedActions = Array.isArray(guard?.recommended_actions) ? guard.recommended_actions : [];
  const warnings = Array.isArray(guard?.warnings) ? guard.warnings : [];

  return (
    <div style={sectionShellStyle(tone)}>
      {modalSectionHeader({
        eyebrow: "Validation Closure Guard",
        title: guard
          ? `Closure Guard: ${label}`
          : loading
          ? "Loading deterministic closure guard"
          : "Closure Guard unavailable",
        description: guard
          ? guard.summary || "OASYS checked deterministic validation guardrails before closure."
          : error || "OASYS is loading task-specific closure guardrails.",
        action: (
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", justifyContent: "flex-end" }}>
            <span style={statusPillStyle(tone)}>{label}</span>
            {typeof onRunValidation === "function" ? (
              <button
                className="btn btn-primary btn-xs"
                type="button"
                onClick={() => onRunValidation(task)}
                disabled={loading || validationActionLoading || isClosedTask(task)}
              >
                {validationActionLoading ? "Running…" : getRunValidationButtonLabel(task)}
              </button>
            ) : null}
            {typeof onOpenValidationDashboard === "function" ? (
              <button
                className="btn btn-ghost btn-xs"
                type="button"
                onClick={() => onOpenValidationDashboard(task)}
                disabled={validationActionLoading}
              >
                Open Validation Dashboard
              </button>
            ) : null}
            {typeof onRefresh === "function" ? (
              <button className="btn btn-ghost btn-xs" type="button" onClick={() => onRefresh(task?.id)} disabled={loading}>
                {loading ? "Refreshing…" : "Refresh"}
              </button>
            ) : null}
          </div>
        ),
      })}

      {error ? <div style={{ color: "var(--danger)", fontSize: 12, lineHeight: 1.45 }}>{error}</div> : null}
      {validationActionError ? (
        <div style={{ color: "var(--danger)", fontSize: 12, lineHeight: 1.45 }}>{validationActionError}</div>
      ) : null}
      {loading && !guard ? <div style={{ color: "var(--muted)", fontSize: 13 }}>Loading closure guard…</div> : null}

      {guard ? (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 8 }}>
            <MetaField label="Decision" value={label} />
            <MetaField label="Confidence" value={confidenceScore ? `${confidenceScore}${confidenceLevel ? ` / ${confidenceLevel}` : ""}` : "—"} />
            <MetaField label="Validation" value={latestValidation.status ? formatValidationLabel(latestValidation.status) : "—"} />
            <MetaField label="Decision Mode" value={formatTitleCase(guard.decision_mode || "deterministic")} />
            <MetaField label="Evidence" value={`${guard.evidence_link_count || evidenceLinks.length || 0} link${(guard.evidence_link_count || evidenceLinks.length || 0) === 1 ? "" : "s"}`} />
            <MetaField label="Guard Version" value={formatGuardVersionLabel(guard.guard_version || guard.guardVersion)} wrap />
          </div>

          <div style={{ color: "var(--text)", fontSize: 13, lineHeight: 1.55 }}>
            <strong>{getLatestValidationLabel(guard)}</strong>
            <br />
            {reason}
          </div>

          {primaryEvidence ? (
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              <span style={statusPillStyle("accent")}>{cleanValue(primaryEvidence.evidence_source, "evidence")}</span>
              <span style={statusPillStyle(normalizeText(primaryEvidence.completeness_status) === "complete" ? "ok" : "warn")}>{formatTitleCase(primaryEvidence.completeness_status || "unknown")}</span>
              <span style={statusPillStyle(normalizeText(primaryEvidence.freshness_status) === "fresh" ? "ok" : "warn")}>{formatTitleCase(primaryEvidence.freshness_status || "unknown")}</span>
              {primaryEvidence.notes ? <span style={{ color: "var(--muted)", fontSize: 12 }}>{formatFrontendCopy(primaryEvidence.notes)}</span> : null}
            </div>
          ) : null}

          {Array.isArray(guard.blocking_reasons) && guard.blocking_reasons.length > 0 ? (
            <div style={{ display: "grid", gap: 6 }}>
              <div style={{ fontSize: 12, fontWeight: 900, color: "var(--danger)", textTransform: "uppercase", letterSpacing: ".05em" }}>
                Blocking Reasons
              </div>
              {guard.blocking_reasons.slice(0, 4).map((item, index) => (
                <div key={`closure-block-${index}`} style={{ color: "var(--muted)", fontSize: 12, lineHeight: 1.45 }}>
                  • {item}
                </div>
              ))}
            </div>
          ) : null}

          {recommendedActions.length > 0 ? (
            <div style={{ display: "grid", gap: 6 }}>
              <div style={{ fontSize: 12, fontWeight: 900, color: "var(--text)", textTransform: "uppercase", letterSpacing: ".05em" }}>
                Recommended Actions
              </div>
              {recommendedActions.slice(0, 3).map((item, index) => (
                <div key={`closure-action-${index}`} style={{ color: "var(--muted)", fontSize: 12, lineHeight: 1.45 }}>
                  • {item}
                </div>
              ))}
            </div>
          ) : null}

          <div
            style={{
              border: "1px solid color-mix(in srgb, var(--accent), transparent 72%)",
              borderRadius: 14,
              padding: 10,
              display: "grid",
              gap: 8,
              background: "color-mix(in srgb, var(--accent), transparent 94%)",
            }}
          >
            <div style={{ fontSize: 12, fontWeight: 900, color: "var(--text)", textTransform: "uppercase", letterSpacing: ".05em" }}>
              Operator Actions
            </div>
            <div style={{ color: "var(--muted)", fontSize: 12, lineHeight: 1.45 }}>
              Complete remediation, run deterministic validation, repair the validation link, then refresh the closure guard.
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {typeof onRunValidation === "function" ? (
                <button
                  className="btn btn-primary btn-xs"
                  type="button"
                  onClick={() => onRunValidation(task)}
                  disabled={loading || validationActionLoading || isClosedTask(task)}
                >
                  {validationActionLoading ? "Running Validation…" : getRunValidationButtonLabel(task)}
                </button>
              ) : null}
              {typeof onRefresh === "function" ? (
                <button
                  className="btn btn-ghost btn-xs"
                  type="button"
                  onClick={() => onRefresh(task?.id)}
                  disabled={loading || validationActionLoading}
                >
                  Refresh Closure Guard
                </button>
              ) : null}
              {typeof onOpenValidationDashboard === "function" ? (
                <button
                  className="btn btn-ghost btn-xs"
                  type="button"
                  onClick={() => onOpenValidationDashboard(task)}
                  disabled={validationActionLoading}
                >
                  Open Validation Dashboard
                </button>
              ) : null}
            </div>
          </div>

          {warnings.length > 0 ? (
            <div style={{ color: "var(--muted)", fontSize: 12, lineHeight: 1.45 }}>
              {formatFrontendCopy(warnings.slice(0, 2).join(" "))}
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

function ClosureReadinessPanel({ task, evidenceRows }) {
  const readiness = closureReadiness(task, evidenceRows);
  const lastValidation = getLastValidation(task);

  return (
    <div style={sectionShellStyle(readiness.tone)}>
      {modalSectionHeader({
        eyebrow: "Closure Workflow",
        title: readiness.label,
        description: readiness.description,
        action: <span style={statusPillStyle(readiness.tone)}>{readiness.label}</span>,
      })}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 8 }}>
        <MetaField label="Task Status" value={getTaskStatusLabel(task.status || "open")} />
        <MetaField label="Validation" value={formatValidationLabel(task.validation_status)} />
        <MetaField label="Linked Evidence" value={`${evidenceRows.length} item${evidenceRows.length === 1 ? "" : "s"}`} />
        <MetaField label="Validated At" value={formatTimestamp(task.validated_at)} />
      </div>

      <ClosureWorkflowSteps task={task} evidenceRows={evidenceRows} />

      {lastValidation ? (() => {
        const latestStatus =
          lastValidation.status ||
          lastValidation.validation_status ||
          lastValidation.result ||
          lastValidation.validation_result ||
          "pending";
        const latestActor = getValidationActorLabel(lastValidation);

        return (
          <div style={{ color: "var(--muted)", fontSize: 12, lineHeight: 1.5 }}>
            Latest validation: <strong style={{ color: "var(--text)" }}>{formatValidationLabel(latestStatus)}</strong>
            {latestActor ? ` by ${latestActor}` : ""}
            {lastValidation.executed_at ? ` · ${formatTimestamp(lastValidation.executed_at)}` : ""}
          </div>
        );
      })() : null}
    </div>
  );
}

function ValidationHistoryPanel({
  task,
  validationHistory,
  loading = false,
  error = "",
  onRefresh,
}) {
  const rows = getValidationRows(task, validationHistory);

  return (
    <div style={sectionShellStyle("accent")}>
      {modalSectionHeader({
        eyebrow: "Validation History",
        title: "Validation Runs",
        description: "Review validation outcomes, failure reasons, manual overrides, and evidence references tied to this remediation task.",
        action:
          typeof onRefresh === "function" ? (
            <button className="btn btn-ghost btn-xs" type="button" onClick={() => onRefresh(task.id)} disabled={loading}>
              {loading ? "Refreshing…" : "Refresh"}
            </button>
          ) : null,
      })}

      {error ? <div style={{ color: "var(--danger)", fontSize: 12, lineHeight: 1.45 }}>{error}</div> : null}
      {loading && rows.length === 0 ? <div style={{ color: "var(--muted)", fontSize: 13 }}>Loading validation history…</div> : null}

      {!loading && rows.length === 0 && !error ? (
        <div style={{ color: "var(--muted)", fontSize: 13, lineHeight: 1.5 }}>
          No validation runs have been recorded for this task yet. Run validation after remediation evidence is ready.
        </div>
      ) : null}

      {rows.length > 0 ? (
        <div style={{ display: "grid", gap: 8 }}>
          {rows.map((run, index) => {
            const status = run.validation_status || run.status || run.validation_result || "pending";
            const tone = validationTone(status);
            const executedAt = run.executed_at || run.created_at || run.updated_at;
            const actor = getValidationActorLabel(run);
            const reason = getValidationRunDetail(run, status);
            const evidenceRef = run.evidence_ref || run.evidence_id || "";

            return (
              <div
                key={run.id || run.validation_run_id || `${status}-${index}`}
                className="open-item-pill"
                style={{
                  cursor: "default",
                  alignItems: "stretch",
                  borderLeft: `3px solid ${tone === "danger" ? "var(--danger)" : tone === "warn" ? "var(--warn)" : tone === "ok" ? "var(--ok)" : "var(--accent)"}`,
                  display: "grid",
                  gap: 6,
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                  <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                    <span style={statusPillStyle(tone)}>{formatValidationLabel(status)}</span>
                    <span style={{ fontWeight: 900, fontSize: 13 }}>{formatTitleCase(cleanValue(run.validator_type, "Validation"))}</span>
                  </div>
                  <div style={{ color: "var(--muted)", fontSize: 12 }}>{formatTimestamp(executedAt)}</div>
                </div>

                <div style={{ color: "var(--muted)", fontSize: 12, display: "flex", gap: 8, flexWrap: "wrap", lineHeight: 1.35 }}>
                  <span>Actor: {actor}</span>
                  {run.control_id ? <span>Control: {run.control_id}</span> : null}
                </div>

                <div style={{ color: tone === "danger" ? "var(--danger)" : "var(--text)", fontSize: 12, lineHeight: 1.45 }}>
                  {reason}
                </div>

                {evidenceRef ? (
                  <div style={{ color: "var(--muted)", fontSize: 12, lineHeight: 1.45, overflowWrap: "anywhere" }}>
                    Evidence reference: <strong style={{ color: "var(--text)" }}>{evidenceRef}</strong>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

function findTaskEvent(events = [], eventType = "") {
  const normalizedType = normalizeText(eventType);
  if (!normalizedType) return null;

  return (Array.isArray(events) ? events : []).find(
    (event) => normalizeText(event?.event_type || event?.eventType || "") === normalizedType
  ) || null;
}

function getEventCreatedAt(event = {}) {
  return event?.created_at || event?.createdAt || event?.event_created_at || event?.timestamp || "";
}

function getEventMetadataValue(event = {}, paths = []) {
  const metadata = asObject(event?.event_metadata || event?.eventMetadata || event?.metadata);
  for (const path of paths) {
    let current = metadata;
    let found = true;

    for (const key of path) {
      if (!current || typeof current !== "object" || !(key in current)) {
        found = false;
        break;
      }
      current = current[key];
    }

    if (found && current !== undefined && current !== null && current !== "") {
      return current;
    }
  }

  return "";
}

function getRecoveryLifecycleSteps(task = {}, events = []) {
  const workflowRecovery = getWorkflowRecoveryMetadata(task);
  const workflow = getWorkflowMetadata(task);
  const lastValidation = getLastValidation(task) || {};
  const validationFailedEvent =
    findTaskEvent(events, "validation_failed") ||
    findTaskEvent(events, "workflow_validation_synced");
  const workflowRecoveredEvent = findTaskEvent(events, "workflow_recovered");
  const validationResetEvent = findTaskEvent(events, "validation_status_reset");

  const validationReason =
    cleanValue(lastValidation.reason || lastValidation.failure_reason, "") ||
    cleanValue(getEventDetail(validationFailedEvent || {}), "") ||
    "Validation failure was recorded against source evidence.";

  const validationFailedAt =
    lastValidation.executed_at ||
    lastValidation.created_at ||
    getEventCreatedAt(validationFailedEvent);

  const recoveredAt =
    workflowRecovery.recovered_at ||
    workflowRecovery.recoveredAt ||
    workflow.last_recovered_at ||
    workflow.lastRecoveredAt ||
    getEventCreatedAt(workflowRecoveredEvent);

  const previousTaskStatus =
    workflowRecovery.previous_task_status ||
    workflowRecovery.previousTaskStatus ||
    getEventMetadataValue(workflowRecoveredEvent, [["workflow_recovery", "previous_task_status"]]) ||
    workflowRecoveredEvent?.from_state ||
    "";

  const nextTaskStatus =
    workflowRecovery.next_task_status ||
    workflowRecovery.nextTaskStatus ||
    getEventMetadataValue(workflowRecoveredEvent, [["workflow_recovery", "next_task_status"]]) ||
    workflowRecoveredEvent?.to_state ||
    task.status ||
    "";

  const previousValidationStatus =
    workflowRecovery.previous_validation_status ||
    workflowRecovery.previousValidationStatus ||
    validationResetEvent?.from_state ||
    getEventMetadataValue(validationResetEvent, [["workflow_recovery", "previous_validation_status"]]) ||
    "";

  const nextValidationStatus =
    workflowRecovery.next_validation_status ||
    workflowRecovery.nextValidationStatus ||
    validationResetEvent?.to_state ||
    getEventMetadataValue(validationResetEvent, [["workflow_recovery", "next_validation_status"]]) ||
    task.validation_status ||
    "";

  const workflowStatus =
    workflow.workflow_status ||
    workflow.workflowStatus ||
    workflow.status ||
    asObject(workflowRecovery.retry).workflow_status ||
    asObject(workflowRecovery.retry).workflowStatus ||
    "";

  const validationStatus = normalizeText(task.validation_status || task.validationStatus || "");
  const taskStatus = normalizeText(task.status || task.task_status || "");
  const hasRecovery = Object.keys(workflowRecovery).length > 0;
  const hasFailureSignal =
    validationStatus === "failed" ||
    normalizeText(lastValidation.result || lastValidation.validation_result || "") === "failed" ||
    Boolean(validationFailedEvent);

  if (!hasRecovery && !hasFailureSignal) return [];

  const steps = [];

  if (hasFailureSignal) {
    steps.push({
      key: "validation-failed",
      tone: "danger",
      label: "Validation failed",
      title: "Source evidence still failed validation.",
      detail: validationReason,
      timestamp: validationFailedAt,
      meta: previousValidationStatus
        ? `Validation moved through ${formatValidationLabel(previousValidationStatus)}.`
        : "Failure evidence is preserved for audit review.",
      complete: true,
    });
  }

  if (hasRecovery) {
    steps.push({
      key: "workflow-recovered",
      tone: "ok",
      label: "Recovered",
      title: "Workflow recovered to active work.",
      detail: workflowRecovery.reason || "Recovery returned the linked remediation workflow to an active queue.",
      timestamp: recoveredAt,
      meta: previousTaskStatus || nextTaskStatus
        ? `${formatWorkflowLabel(previousTaskStatus)} → ${formatWorkflowLabel(nextTaskStatus)}`
        : "Task was returned to active remediation work.",
      complete: true,
    });

    steps.push({
      key: "validation-reset",
      tone: "warn",
      label: "Pending",
      title: "Validation reset for the next remediation pass.",
      detail: "The prior failure remains in history, while current validation is pending re-check.",
      timestamp: getEventCreatedAt(validationResetEvent) || recoveredAt,
      meta: previousValidationStatus || nextValidationStatus
        ? `${formatValidationLabel(previousValidationStatus)} → ${formatValidationLabel(nextValidationStatus)}`
        : `Current validation is ${formatValidationLabel(task.validation_status)}.`,
      complete: true,
    });
  }

  steps.push({
    key: "current-state",
    tone: validationStatus === "pending" || taskStatus === "in_progress" ? "accent" : validationTone(validationStatus),
    label: "Current",
    title: "Current lifecycle state.",
    detail:
      taskStatus === "in_progress" && validationStatus === "pending"
        ? "Task is back in active work and waiting for the next validation run."
        : `Task is ${getTaskStatusLabel(task.status || "open")} with validation ${formatValidationLabel(task.validation_status)}.`,
    timestamp: task.updated_at || task.updatedAt || recoveredAt,
    meta: workflowStatus ? `Workflow: ${formatWorkflowLabel(workflowStatus)}` : "",
    complete: false,
  });

  return steps;
}

function RecoveryLifecycleTimeline({ task, events = [], loading = false }) {
  const steps = getRecoveryLifecycleSteps(task, events);
  if (steps.length === 0) return null;

  const workflowRecovery = getWorkflowRecoveryMetadata(task);
  const hasRecovery = Object.keys(workflowRecovery).length > 0;
  const currentValidation = formatValidationLabel(task.validation_status || "pending");
  const currentStatus = getTaskStatusLabel(task.status || "open");

  return (
    <div style={sectionShellStyle(hasRecovery ? "ok" : "warn")}>
      {modalSectionHeader({
        eyebrow: "Recovery Lifecycle",
        title: hasRecovery
          ? "Validation failed → recovery recorded → task returned to active work."
          : "Validation issue recorded.",
        description: hasRecovery
          ? `Current state: ${currentStatus} with ${currentValidation} validation. Historical failure evidence remains preserved below.`
          : "Lifecycle events will show recovery and re-validation progress as the task moves forward.",
        action: <span style={statusPillStyle(hasRecovery ? "ok" : "warn")}>{hasRecovery ? "Recovered" : "In Review"}</span>,
      })}

      {loading ? (
        <div style={{ color: "var(--muted)", fontSize: 12, lineHeight: 1.45 }}>
          Loading lifecycle events…
        </div>
      ) : null}

      <div style={{ display: "grid", gap: 8 }}>
        {steps.map((step, index) => (
          <div
            key={step.key}
            className="open-item-pill"
            style={{
              cursor: "default",
              alignItems: "stretch",
              display: "grid",
              gap: 6,
              borderLeft: `3px solid ${
                step.tone === "danger"
                  ? "var(--danger)"
                  : step.tone === "warn"
                  ? "var(--warn)"
                  : step.tone === "ok"
                  ? "var(--ok)"
                  : step.tone === "accent"
                  ? "var(--accent)"
                  : "color-mix(in srgb, var(--text), transparent 70%)"
              }`,
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <span style={{ color: "var(--muted)", fontSize: 11, fontWeight: 900 }}>Step {index + 1}</span>
                <span style={statusPillStyle(step.tone)}>{step.label}</span>
                <strong style={{ fontSize: 13 }}>{step.title}</strong>
              </div>
              <div style={{ color: "var(--muted)", fontSize: 12 }}>{formatTimestamp(step.timestamp)}</div>
            </div>

            <div style={{ color: "var(--muted)", fontSize: 12, lineHeight: 1.45 }}>
              {step.detail}
            </div>

            {step.meta ? (
              <div style={{ color: "var(--text)", fontSize: 12, lineHeight: 1.4, fontWeight: 850 }}>
                {step.meta}
              </div>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
}


function slugifyEvidenceReference(value = "") {
  const cleaned = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\.[a-z0-9]{1,8}$/i, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

  return cleaned || "evidence-reference";
}

function getEvidenceTypeLabel(value = "") {
  const normalized = normalizeText(value || "note");
  if (normalized === "file_reference") return "File Reference";
  if (normalized === "storage_path") return "Storage Path";
  if (normalized === "screenshot") return "Screenshot";
  if (normalized === "log") return "Log";
  if (normalized === "export") return "Export";
  if (normalized === "ticket") return "Ticket";
  if (normalized === "other") return "Other";
  return "Note";
}

function evidenceTypeNeedsManualReference(value = "") {
  const normalized = normalizeText(value || "note");
  return normalized === "file_reference" || normalized === "storage_path" || normalized === "ticket";
}

function evidenceTypeSupportsLocalFile(value = "") {
  const normalized = normalizeText(value || "note");
  return normalized === "screenshot" || normalized === "log" || normalized === "export";
}

function getEvidenceReferenceLabel(value = "") {
  const normalized = normalizeText(value || "note");
  if (normalized === "ticket") return "Ticket URL / Ticket ID";
  if (normalized === "screenshot") return "Screenshot Reference";
  if (normalized === "log") return "Log Reference";
  if (normalized === "export") return "Export Reference";
  if (normalized === "note") return "Manual Note Reference";
  if (normalized === "file_reference") return "File Reference / URL";
  if (normalized === "storage_path") return "Storage Path / Reference";
  return "Storage Path / Reference";
}

function getEvidenceLabelFieldLabel(value = "") {
  const normalized = normalizeText(value || "note");
  if (normalized === "note") return "Note Label";
  if (normalized === "ticket") return "Ticket Label";
  if (normalized === "screenshot") return "Screenshot Label";
  if (normalized === "log") return "Log Label";
  if (normalized === "export") return "Export Label";
  return "Filename / Label";
}

function getEvidenceReferencePlaceholder(value = "") {
  const normalized = normalizeText(value || "note");
  if (normalized === "ticket") return "example: https://ticket/123 or JIRA-123";
  if (normalized === "screenshot") return "manual://screenshot/mfa-proof or archive://evidence-id";
  if (normalized === "log") return "manual://log/cloudtrail-review or s3://bucket/path/log.txt";
  if (normalized === "export") return "manual://export/credential-report or s3://bucket/path/export.csv";
  if (normalized === "note") return "manual://note/owner-approval";
  return "manual://reference, s3://bucket/key, https://ticket, or archive://evidence-id";
}

function buildEvidenceReferenceFromDraft(draft = {}) {
  const evidenceType = normalizeText(draft.evidence_type || "note");
  const label =
    cleanValue(draft.filename, "") ||
    cleanValue(draft.notes, "") ||
    cleanValue(draft.selected_file_name, "") ||
    "evidence-reference";
  const slug = slugifyEvidenceReference(label);

  if (evidenceType === "screenshot") return `manual://screenshot/${slug}`;
  if (evidenceType === "log") return `manual://log/${slug}`;
  if (evidenceType === "export") return `manual://export/${slug}`;
  if (evidenceType === "ticket") return `manual://ticket/${slug}`;
  if (evidenceType === "file_reference") return `manual://file-reference/${slug}`;
  if (evidenceType === "storage_path") return `manual://storage-reference/${slug}`;
  if (evidenceType === "other") return `manual://other/${slug}`;
  return `manual://note/${slug}`;
}

function buildDefaultEvidenceDraft(task = {}) {
  return {
    evidence_type: "note",
    filename: "",
    storage_path: "",
    sha256: "",
    linked_control_id: cleanValue(task.control_id, ""),
    linked_finding_id: cleanValue(task.finding_id || task.findingId, ""),
    notes: "",
    selected_file_name: "",
    selected_file_size: null,
    selected_file_type: "",
  };
}

function AttachEvidenceForm({ task, onCancel, onSubmit, creating = false }) {
  const [draft, setDraft] = React.useState(() => buildDefaultEvidenceDraft(task));
  const [localError, setLocalError] = React.useState("");

  React.useEffect(() => {
    setDraft(buildDefaultEvidenceDraft(task));
    setLocalError("");
  }, [task]);

  const evidenceType = normalizeText(draft.evidence_type || "note");
  const supportsLocalFile = evidenceTypeSupportsLocalFile(evidenceType);
  const requiresManualReference = evidenceTypeNeedsManualReference(evidenceType);
  const referenceLabel = getEvidenceReferenceLabel(evidenceType);
  const labelFieldLabel = getEvidenceLabelFieldLabel(evidenceType);
  const referencePlaceholder = getEvidenceReferencePlaceholder(evidenceType);
  const computedReference = String(draft.storage_path || "").trim() || buildEvidenceReferenceFromDraft(draft);

  const updateDraft = (key, value) => {
    setDraft((current) => {
      const next = { ...(current || {}), [key]: value };
      const nextType = normalizeText(next.evidence_type || "note");
      const shouldAutoReference = !evidenceTypeNeedsManualReference(nextType);

      if (key === "evidence_type") {
        next.storage_path = shouldAutoReference ? buildEvidenceReferenceFromDraft(next) : "";
      }

      if ((key === "filename" || key === "notes") && shouldAutoReference) {
        next.storage_path = buildEvidenceReferenceFromDraft(next);
      }

      return next;
    });
    setLocalError("");
  };

  const handleFileSelected = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setDraft((current) => {
      const next = {
        ...(current || {}),
        filename: current?.filename || file.name,
        selected_file_name: file.name,
        selected_file_size: file.size,
        selected_file_type: file.type || "",
      };
      next.storage_path = current?.storage_path || buildEvidenceReferenceFromDraft(next);
      return next;
    });
    setLocalError("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLocalError("");

    const evidenceTypeValue = String(draft.evidence_type || "note").trim() || "note";
    const filename = String(draft.filename || "").trim();
    const notes = String(draft.notes || "").trim();
    const storagePath = String(draft.storage_path || "").trim() || buildEvidenceReferenceFromDraft(draft);

    if (!filename) {
      setLocalError(
        evidenceTypeValue === "note"
          ? "Enter a short note label before attaching evidence."
          : "Enter an evidence filename or short label."
      );
      return;
    }

    if (requiresManualReference && !String(draft.storage_path || "").trim()) {
      setLocalError(`${referenceLabel} is required for ${getEvidenceTypeLabel(evidenceTypeValue)} evidence.`);
      return;
    }

    if (!storagePath) {
      setLocalError("A manual evidence reference could not be generated. Enter a reference manually.");
      return;
    }

    if (typeof onSubmit !== "function") {
      setLocalError("Attach Evidence is not wired yet.");
      return;
    }

    const metadata = {
      source: "remediation_task_modal",
      evidence_trust_level: "manual_user_provided",
      verification_status: "unverified_user_attached",
      reference_mode: supportsLocalFile ? "local_file_reference" : requiresManualReference ? "manual_reference" : "auto_manual_reference",
      generated_reference: !String(draft.storage_path || "").trim(),
    };

    if (notes) metadata.notes = notes;
    if (draft.selected_file_name) {
      metadata.selected_file_name = draft.selected_file_name;
      metadata.selected_file_size = draft.selected_file_size;
      metadata.selected_file_type = draft.selected_file_type;
      metadata.file_upload_status = "not_uploaded_reference_only";
    }

    const payload = {
      evidence_type: evidenceTypeValue,
      filename,
      storage_path: storagePath,
      sha256: String(draft.sha256 || "").trim() || null,
      linked_control_id: String(draft.linked_control_id || "").trim() || null,
      linked_finding_id: String(draft.linked_finding_id || "").trim() || null,
      metadata,
    };

    try {
      await onSubmit(payload);
      setDraft(buildDefaultEvidenceDraft(task));
      if (typeof onCancel === "function") onCancel();
    } catch (error) {
      setLocalError(String(error?.message || error || "Unable to attach evidence."));
    }
  };

  const fieldStyle = {
    border: "1px solid color-mix(in srgb, var(--text), transparent 82%)",
    borderRadius: 12,
    background: "rgba(15, 23, 42, 0.72)",
    color: "var(--text)",
    padding: "10px 12px",
    minWidth: 0,
  };

  const labelBaseStyle = {
    display: "grid",
    gap: 6,
    fontSize: 12,
    color: "var(--muted)",
    fontWeight: 800,
  };

  return (
    <form
      onSubmit={handleSubmit}
      style={{
        border: "1px solid color-mix(in srgb, var(--accent), transparent 72%)",
        background: "color-mix(in srgb, var(--accent), transparent 94%)",
        borderRadius: 14,
        padding: 12,
        display: "grid",
        gap: 10,
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
        <div>
          <div style={{ fontWeight: 900, fontSize: 13 }}>Attach Evidence</div>
          <div style={{ color: "var(--muted)", fontSize: 12, lineHeight: 1.45, marginTop: 2 }}>
            Add a note, screenshot reference, file reference, storage path, ticket, or validation artifact to this remediation task.
          </div>
        </div>
        <span style={statusPillStyle("accent")}>Task evidence</span>
      </div>

      <div
        style={{
          border: "1px solid color-mix(in srgb, var(--warn), transparent 64%)",
          borderRadius: 12,
          background: "color-mix(in srgb, var(--warn), transparent 92%)",
          color: "var(--warn)",
          padding: "9px 10px",
          fontSize: 12,
          lineHeight: 1.45,
        }}
      >
        <strong>Trust label:</strong> Manual Evidence · User Provided. This documents the remediation record, but OASYS will not treat it as verified source proof unless validation passes, an approved exception is recorded, or a manual override is approved.
      </div>

      {localError ? (
        <div style={{ color: "var(--danger)", fontSize: 12, lineHeight: 1.45, fontWeight: 750 }}>
          {localError}
        </div>
      ) : null}

      <div style={{ display: "grid", gridTemplateColumns: "minmax(120px, .5fr) minmax(220px, 1fr)", gap: 10 }}>
        <label style={labelBaseStyle}>
          Evidence Type
          <select
            value={draft.evidence_type}
            onChange={(event) => updateDraft("evidence_type", event.target.value)}
            disabled={creating}
            style={fieldStyle}
          >
            <option value="note">Note</option>
            <option value="file_reference">File Reference</option>
            <option value="storage_path">Storage Path</option>
            <option value="screenshot">Screenshot</option>
            <option value="log">Log</option>
            <option value="export">Export</option>
            <option value="ticket">Ticket</option>
            <option value="other">Other</option>
          </select>
        </label>

        <label style={labelBaseStyle}>
          {labelFieldLabel}
          <input
            type="text"
            value={draft.filename}
            onChange={(event) => updateDraft("filename", event.target.value)}
            placeholder={evidenceType === "note" ? "example: owner approval note" : "example: iam-role-remediation-proof.txt"}
            disabled={creating}
            style={fieldStyle}
          />
        </label>
      </div>

      {supportsLocalFile ? (
        <label style={labelBaseStyle}>
          Choose {getEvidenceTypeLabel(evidenceType)} File
          <input
            type="file"
            accept={evidenceType === "screenshot" ? "image/*" : undefined}
            onChange={handleFileSelected}
            disabled={creating}
            style={fieldStyle}
          />
          <span style={{ color: "var(--muted)", fontSize: 11, lineHeight: 1.45 }}>
            Current build stores a manual file reference first. Backend upload/archive storage will replace this with a verified object reference later.
            {draft.selected_file_name ? ` Selected: ${draft.selected_file_name}` : ""}
          </span>
        </label>
      ) : null}

      <label style={labelBaseStyle}>
        {referenceLabel}
        <input
          type="text"
          value={draft.storage_path || computedReference}
          onChange={(event) => updateDraft("storage_path", event.target.value)}
          placeholder={referencePlaceholder}
          disabled={creating}
          style={fieldStyle}
        />
        <span style={{ color: "var(--muted)", fontSize: 11, lineHeight: 1.45 }}>
          {requiresManualReference
            ? "Required for this evidence type. Use a ticket URL/ID, document link, s3:// path, archive:// reference, or manual:// reference."
            : "OASYS can auto-create this reference from the label. You can edit it if you already have a ticket URL, s3:// path, or archive:// evidence id."}
          <br />
          Examples: manual://screenshot/mfa-proof, manual://note/owner-approval, https://ticket/123, s3://bucket/path/file, archive://evidence-id.
        </span>
      </label>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 10 }}>
        <label style={labelBaseStyle}>
          Linked Control
          <input
            type="text"
            value={draft.linked_control_id}
            onChange={(event) => updateDraft("linked_control_id", event.target.value)}
            placeholder="Optional control id"
            disabled={creating}
            style={fieldStyle}
          />
        </label>

        <label style={labelBaseStyle}>
          Linked Finding
          <input
            type="text"
            value={draft.linked_finding_id}
            onChange={(event) => updateDraft("linked_finding_id", event.target.value)}
            placeholder="Optional finding id"
            disabled={creating}
            style={fieldStyle}
          />
        </label>
      </div>

      <label style={labelBaseStyle}>
        SHA-256
        <input
          type="text"
          value={draft.sha256}
          onChange={(event) => updateDraft("sha256", event.target.value)}
          placeholder="Optional integrity hash"
          disabled={creating}
          style={fieldStyle}
        />
      </label>

      <label style={labelBaseStyle}>
        Notes
        <textarea
          rows={3}
          value={draft.notes}
          onChange={(event) => updateDraft("notes", event.target.value)}
          placeholder="Optional audit note explaining what this evidence proves."
          disabled={creating}
          style={{ ...fieldStyle, resize: "vertical" }}
        />
      </label>

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, flexWrap: "wrap" }}>
        <button className="btn btn-ghost btn-xs" type="button" onClick={onCancel} disabled={creating}>
          Cancel
        </button>
        <button className="btn btn-primary btn-xs" type="submit" disabled={creating}>
          {creating ? "Attaching…" : "Attach Evidence"}
        </button>
      </div>
    </form>
  );
}

function EvidencePanel({
  task,
  evidence,
  loading = false,
  error = "",
  onRefresh,
  onCreateEvidence,
  creating = false,
  createError = "",
}) {
  const rows = getEvidenceRows(evidence);
  const [showAttachForm, setShowAttachForm] = React.useState(false);
  const canAttachEvidence = typeof onCreateEvidence === "function" && !isClosedTask(task);

  React.useEffect(() => {
    setShowAttachForm(false);
  }, [task?.id]);

  const handleCreateEvidence = async (payload) => {
    await onCreateEvidence(payload);
    if (typeof onRefresh === "function") {
      await onRefresh(task.id);
    }
  };

  return (
    <div style={sectionShellStyle(rows.length > 0 ? "ok" : "warn")}>
      {modalSectionHeader({
        eyebrow: "Linked Evidence",
        title: "Evidence Record",
        description: "Review supporting evidence attached to this task before validation and closure.",
        action: (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
            {canAttachEvidence ? (
              <button
                className="btn btn-ghost btn-xs"
                type="button"
                onClick={() => setShowAttachForm((current) => !current)}
                disabled={creating}
              >
                {showAttachForm ? "Hide Form" : "Attach Evidence"}
              </button>
            ) : null}
            {typeof onRefresh === "function" ? (
              <button className="btn btn-ghost btn-xs" type="button" onClick={() => onRefresh(task.id)} disabled={loading || creating}>
                {loading ? "Refreshing…" : "Refresh"}
              </button>
            ) : null}
          </div>
        ),
      })}

      {error ? <div style={{ color: "var(--danger)", fontSize: 12, lineHeight: 1.45 }}>{error}</div> : null}
      {createError ? <div style={{ color: "var(--danger)", fontSize: 12, lineHeight: 1.45 }}>{createError}</div> : null}

      {showAttachForm && canAttachEvidence ? (
        <AttachEvidenceForm
          task={task}
          creating={creating}
          onSubmit={handleCreateEvidence}
          onCancel={() => setShowAttachForm(false)}
        />
      ) : null}

      {loading && rows.length === 0 ? <div style={{ color: "var(--muted)", fontSize: 13 }}>Loading linked evidence…</div> : null}

      {!loading && rows.length === 0 && !error ? (
        <div style={{ color: "var(--muted)", fontSize: 13, lineHeight: 1.5 }}>
          No evidence is linked to this task yet. Attach a note, file reference, storage path, or validation artifact before closure when possible.
        </div>
      ) : null}

      {rows.length > 0 ? (
        <div style={{ display: "grid", gap: 8 }}>
          {rows.map((item, index) => {
            const uploadedAt = item.uploaded_at || item.created_at || item.updated_at;
            const actor = cleanValue(item.uploaded_by_name || item.uploaded_by, "System");
            const title = cleanValue(item.filename || item.evidence_type || item.storage_path, "Evidence item");
            const trustProfile = getEvidenceTrustProfile(item);

            return (
              <div
                key={item.id || `${title}-${index}`}
                className="open-item-pill"
                style={{ cursor: "default", alignItems: "stretch", display: "grid", gap: 6 }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                  <div style={{ display: "flex", gap: 8, alignItems: "center", minWidth: 0, flexWrap: "wrap" }}>
                    <span style={statusPillStyle("ok")}>{cleanValue(item.evidence_type, "evidence").replace(/_/g, " ")}</span>
                    <span style={statusPillStyle(trustProfile.tone)}>{trustProfile.label}</span>
                    <span style={{ fontWeight: 900, fontSize: 13, overflowWrap: "anywhere" }}>{title}</span>
                  </div>
                  <div style={{ color: "var(--muted)", fontSize: 12 }}>{formatTimestamp(uploadedAt)}</div>
                </div>

                <div style={{ color: "var(--muted)", fontSize: 12, display: "flex", gap: 8, flexWrap: "wrap", lineHeight: 1.35 }}>
                  <span>Uploaded by: {actor}</span>
                  {item.linked_control_id ? <span>Control: {item.linked_control_id}</span> : null}
                  {item.linked_finding_id ? <span>Finding: {item.linked_finding_id}</span> : null}
                </div>

                <div style={{ color: "var(--muted)", fontSize: 12, lineHeight: 1.45 }}>
                  Trust: <strong style={{ color: "var(--text)" }}>{trustProfile.label}</strong> — {trustProfile.detail}
                </div>

                {item.storage_path ? (
                  <div style={{ color: "var(--muted)", fontSize: 12, lineHeight: 1.45, overflowWrap: "anywhere" }}>
                    Storage path: <strong style={{ color: "var(--text)" }}>{item.storage_path}</strong>
                  </div>
                ) : null}

                {item.sha256 ? (
                  <div style={{ color: "var(--muted)", fontSize: 12, lineHeight: 1.45, overflowWrap: "anywhere" }}>
                    SHA-256: <strong style={{ color: "var(--text)" }}>{item.sha256}</strong>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

export default function RemediationTaskModal({
  framework,
  managedTask,
  managedTaskDraft,
  setManagedTaskDraft,
  managedTaskInvestigationDraft,
  setManagedTaskInvestigationDraft,
  managedTaskInvestigationSaving,
  managedTaskEvents,
  managedTaskEventsLoading,
  managedTaskEventsError,
  remediationTaskUpdating,
  handleCloseManageTask,
  handleSaveManagedTask,
  handleSaveManagedTaskInvestigation,
  handleMarkTaskReadyForValidation,
  handleLoadManagedTaskEvents,
  handleOpenFixPlan,
  handleOpenWorkflowCenter,
  handleRecoverManagedTaskWorkflow,
  managedTaskWorkflowRecovering = false,
  managedTaskWorkflowRecoveryError = "",
  managedTaskValidationHistory = [],
  managedTaskValidationHistoryLoading = false,
  managedTaskValidationHistoryError = "",
  handleLoadManagedTaskValidationHistory,
  managedTaskEvidence = [],
  managedTaskEvidenceLoading = false,
  managedTaskEvidenceError = "",
  handleLoadManagedTaskEvidence,
  handleCreateManagedTaskEvidence,
  managedTaskEvidenceCreating = false,
  managedTaskEvidenceCreateError = "",
  managedTaskClosureGuard = null,
  managedTaskClosureGuardLoading = false,
  managedTaskClosureGuardError = "",
  handleLoadManagedTaskClosureGuard,
  managedTaskValidationActionRunning = false,
  managedTaskValidationActionError = "",
  handleRunManagedTaskValidation,
  handleOpenValidationDashboard,
}) {
  const closureSectionRef = React.useRef(null);
  const validationSectionRef = React.useRef(null);
  const workflowSectionRef = React.useRef(null);
  const evidenceSectionRef = React.useRef(null);
  const auditSectionRef = React.useRef(null);
  const closureReasonFieldRef = React.useRef(null);

  const scrollToSection = React.useCallback((ref) => {
    window.requestAnimationFrame(() => {
      ref?.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }, []);

  const focusClosureReasonField = React.useCallback(() => {
    window.requestAnimationFrame(() => {
      closureReasonFieldRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      window.setTimeout(() => {
        closureReasonFieldRef.current?.focus?.({ preventScroll: true });
      }, 180);
    });
  }, []);

  if (!managedTask || !managedTaskDraft) return null;

  const validationRows = getValidationRows(managedTask, managedTaskValidationHistory);
  const evidenceRows = getEvidenceRows(managedTaskEvidence);
  const currentTaskStatus = normalizeText(managedTask.status);
  const draftTaskStatus = normalizeText(managedTaskDraft.status);
  const approvedExceptionAccepted = isApprovedExceptionTask(managedTask);
  const evidenceAttached = evidenceRows.length > 0;
  const canDirectValidateAndClose = Boolean(
    approvedExceptionAccepted &&
      evidenceAttached &&
      !isClosedTask(managedTask) &&
      currentTaskStatus !== "validated"
  );
  const isClosingTask = draftTaskStatus === "closed" && !isClosedTask(managedTask);
  const taskCanBeClosed = currentTaskStatus === "validated" || canDirectValidateAndClose;
  const closureReasonText = String(managedTaskDraft.closure_reason || "").trim();
  const closureReasonMissing = isClosingTask && !closureReasonText;
  const validateAndCloseReasonMissing = canDirectValidateAndClose && !closureReasonText;
  const closureStatusBlocked = isClosingTask && !taskCanBeClosed;
  const saveTaskDisabled = Boolean(
    remediationTaskUpdating || closureReasonMissing || closureStatusBlocked
  );
  const closureReasonNeedsAttention = Boolean(validateAndCloseReasonMissing || closureReasonMissing);
  const closureReasonWarningButtonStyle = closureReasonNeedsAttention
    ? {
        color: "var(--warn)",
        border: "1px solid color-mix(in srgb, var(--warn), transparent 42%)",
        background: "color-mix(in srgb, var(--warn), transparent 90%)",
        boxShadow: "0 0 0 2px color-mix(in srgb, var(--warn), transparent 90%)",
        cursor: remediationTaskUpdating ? "not-allowed" : "pointer",
      }
    : undefined;

  const handleValidateAndClose = () => {
    if (!canDirectValidateAndClose || validateAndCloseReasonMissing || remediationTaskUpdating) return;

    const nextDraft = {
      ...(managedTaskDraft || {}),
      status: "closed",
      closure_reason: closureReasonText,
    };

    setManagedTaskDraft(nextDraft);

    handleSaveManagedTask(
      nextDraft,
      "Approved exception validated and closed."
    );
  };

  const handleValidateAndCloseClick = () => {
    if (validateAndCloseReasonMissing) {
      focusClosureReasonField();
      return;
    }

    handleValidateAndClose();
  };

  return (
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Manage remediation task"
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 118,
              background: "rgba(2, 6, 23, 0.76)",
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "center",
              padding: "72px 24px 24px",
              overflowY: "auto",
            }}
            onClick={handleCloseManageTask}
          >
            <section
              className="card"
              style={{
                width: "min(760px, calc(100vw - 48px))",
                maxHeight: "calc(100vh - 104px)",
                overflow: "auto",
                boxShadow: "0 24px 80px rgba(0,0,0,0.48)",
              }}
              onClick={(event) => event.stopPropagation()}
            >
              <div className="card-header">
                <div>
                  <div className="dash-eyebrow">
                    {isClosedTask(managedTask)
                      ? "OASYS · Closed Remediation Task"
                      : "OASYS · Remediation Task"}
                  </div>
                  <div className="card-title">
                    {managedTask.title || buildControlLabel(managedTask.control_id, managedTask.rule_id)}
                  </div>
                  <div
                    style={{
                      color: "var(--muted)",
                      fontSize: 13,
                      lineHeight: 1.45,
                      marginTop: 4,
                    }}
                  >
                    {isClosedTask(managedTask)
                      ? "This remediation task is closed. Lifecycle fields are locked to preserve the audit trail."
                      : "Assign ownership, set priority, and move this remediation item through its workflow."}
                  </div>
                </div>

                <button
                  className="btn btn-ghost btn-xs"
                  type="button"
                  onClick={handleCloseManageTask}
                  disabled={remediationTaskUpdating}
                >
                  Close
                </button>
              </div>

              <div className="card-body" style={{ display: "grid", gap: 14 }}>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                    gap: 8,
                  }}
                >
                  <MetaField label="Task ID" value={managedTask.id} wrap />
                  <MetaField label="Rule" value={managedTask.rule_id || "—"} wrap />
                  <MetaField label="Control" value={managedTask.control_id || "—"} />
                  <MetaField label="Validation" value={formatValidationLabel(managedTask.validation_status || "pending")} />
                </div>

                <div ref={workflowSectionRef}>
                  <WorkflowOrchestrationPanel
                    task={managedTask}
                    onOpenWorkflowCenter={handleOpenWorkflowCenter}
                    onRecoverWorkflow={handleRecoverManagedTaskWorkflow}
                    recovering={managedTaskWorkflowRecovering || remediationTaskUpdating}
                    recoveryError={managedTaskWorkflowRecoveryError}
                  />
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                    gap: 12,
                  }}
                >
                  <label style={{ display: "grid", gap: 6, fontSize: 12, color: "var(--muted)", fontWeight: 800 }}>
                    Status
                    {isClosedTask(managedTask) ? (
                      <div
                        className="open-item-pill"
                        style={{
                          cursor: "default",
                          minHeight: 42,
                          alignItems: "center",
                          borderColor: "color-mix(in srgb, var(--ok), transparent 68%)",
                          background: "color-mix(in srgb, var(--ok), transparent 92%)",
                        }}
                      >
                        <div className="open-item-label">Task Status</div>
                        <div
                          className="open-item-value"
                          style={{
                            color: "var(--ok)",
                            fontWeight: 900,
                            textAlign: "right",
                          }}
                        >
                          Closed
                        </div>
                      </div>
                    ) : (
                      <select
                        value={managedTaskDraft.status}
                        onChange={(event) =>
                          setManagedTaskDraft((current) => ({
                            ...(current || {}),
                            status: event.target.value,
                          }))
                        }
                        style={{
                          border: "1px solid color-mix(in srgb, var(--text), transparent 82%)",
                          borderRadius: 12,
                          background: "rgba(15, 23, 42, 0.72)",
                          color: "var(--text)",
                          padding: "10px 12px",
                        }}
                      >
                        <option value="open">Open</option>
                        <option value="in_progress">In Progress</option>
                        <option value="blocked">Blocked</option>
                        <option value="ready_for_validation">Ready For Validation</option>
                        <option value="validated">Validated</option>
                        {currentTaskStatus === "validated" ||
                        draftTaskStatus === "closed" ||
                        canDirectValidateAndClose ? (
                          <option value="closed">Closed</option>
                        ) : null}
                      </select>
                    )}
                  </label>

                  <label style={{ display: "grid", gap: 6, fontSize: 12, color: "var(--muted)", fontWeight: 800 }}>
                    Priority
                    <input
                      type="number"
                      min="1"
                      max="999"
                      value={managedTaskDraft.priority}
                      onChange={(event) =>
                        setManagedTaskDraft((current) => ({
                          ...(current || {}),
                          priority: event.target.value,
                        }))
                      }
                      disabled={isClosedTask(managedTask)}
                      style={{
                        border: "1px solid color-mix(in srgb, var(--text), transparent 82%)",
                        borderRadius: 12,
                        background: "rgba(15, 23, 42, 0.72)",
                        color: "var(--text)",
                        padding: "10px 12px",
                      }}
                    />
                  </label>

                  <label style={{ display: "grid", gap: 6, fontSize: 12, color: "var(--muted)", fontWeight: 800 }}>
                    Owner
                    <input
                      type="text"
                      value={managedTaskDraft.owner_name}
                      placeholder="Owner name"
                      onChange={(event) =>
                        setManagedTaskDraft((current) => ({
                          ...(current || {}),
                          owner_name: event.target.value,
                        }))
                      }
                      disabled={isClosedTask(managedTask)}
                      style={{
                        border: "1px solid color-mix(in srgb, var(--text), transparent 82%)",
                        borderRadius: 12,
                        background: "rgba(15, 23, 42, 0.72)",
                        color: "var(--text)",
                        padding: "10px 12px",
                      }}
                    />
                  </label>

                  <label style={{ display: "grid", gap: 6, fontSize: 12, color: "var(--muted)", fontWeight: 800 }}>
                    Due Date
                    <input
                      type="date"
                      value={managedTaskDraft.due_date}
                      onChange={(event) =>
                        setManagedTaskDraft((current) => ({
                          ...(current || {}),
                          due_date: event.target.value,
                        }))
                      }
                      disabled={isClosedTask(managedTask)}
                      style={{
                        border: "1px solid color-mix(in srgb, var(--text), transparent 82%)",
                        borderRadius: 12,
                        background: "rgba(15, 23, 42, 0.72)",
                        color: "var(--text)",
                        padding: "10px 12px",
                      }}
                    />
                  </label>
                </div>

                {normalizeText(managedTaskDraft.status) === "blocked" ? (
                  <label style={{ display: "grid", gap: 6, fontSize: 12, color: "var(--muted)", fontWeight: 800 }}>
                    Block Reason
                    <textarea
                      value={managedTaskDraft.block_reason}
                      placeholder="What is blocking this task?"
                      rows={3}
                      onChange={(event) =>
                        setManagedTaskDraft((current) => ({
                          ...(current || {}),
                          block_reason: event.target.value,
                        }))
                      }
                      style={{
                        border: "1px solid color-mix(in srgb, var(--text), transparent 82%)",
                        borderRadius: 12,
                        background: "rgba(15, 23, 42, 0.72)",
                        color: "var(--text)",
                        padding: "10px 12px",
                        resize: "vertical",
                      }}
                    />
                  </label>
                ) : null}

                {draftTaskStatus === "closed" || isClosedTask(managedTask) || canDirectValidateAndClose ? (
                  <label style={{ display: "grid", gap: 6, fontSize: 12, color: "var(--muted)", fontWeight: 800 }}>
                    Closure Reason {isClosedTask(managedTask) ? "" : canDirectValidateAndClose ? "(required for Validate & Close)" : "(required)"}
                    <textarea
                      ref={closureReasonFieldRef}
                      value={managedTaskDraft.closure_reason}
                      placeholder="Document why this validated task is being closed for the audit trail."
                      rows={3}
                      onChange={(event) =>
                        setManagedTaskDraft((current) => ({
                          ...(current || {}),
                          closure_reason: event.target.value,
                        }))
                      }
                      readOnly={isClosedTask(managedTask)}
                      aria-invalid={closureReasonNeedsAttention ? "true" : "false"}
                      style={{
                        border: closureReasonNeedsAttention
                          ? "1px solid color-mix(in srgb, var(--warn), transparent 34%)"
                          : "1px solid color-mix(in srgb, var(--text), transparent 82%)",
                        borderRadius: 12,
                        background: closureReasonNeedsAttention
                          ? "color-mix(in srgb, var(--warn), transparent 93%)"
                          : "rgba(15, 23, 42, 0.72)",
                        color: "var(--text)",
                        padding: "10px 12px",
                        resize: "vertical",
                        boxShadow: closureReasonNeedsAttention
                          ? "0 0 0 3px color-mix(in srgb, var(--warn), transparent 86%)"
                          : "none",
                      }}
                    />
                    {closureReasonNeedsAttention ? (
                      <div style={{ color: "var(--warn)", fontSize: 12, lineHeight: 1.45, fontWeight: 800 }}>
                        Add a closure reason to enable Validate & Close.
                      </div>
                    ) : null}
                    {closureStatusBlocked ? (
                      <div style={{ color: "var(--warn)", fontSize: 12, lineHeight: 1.45 }}>
                        This task must be saved as Validated before it can be closed, unless it is being finalized through the approved-exception Validate & Close path.
                      </div>
                    ) : null}
                  </label>
                ) : null}

                <div
                  style={{
                    border: "1px solid color-mix(in srgb, var(--text), transparent 86%)",
                    borderRadius: 16,
                    padding: 12,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 10,
                    flexWrap: "wrap",
                    background: "color-mix(in srgb, var(--panel), transparent 30%)",
                  }}
                >
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                    <SectionJumpButton label="Closure" onClick={() => scrollToSection(closureSectionRef)} />
                    <SectionJumpButton label="Validation History" onClick={() => scrollToSection(validationSectionRef)} />
                    <SectionJumpButton label="Evidence" onClick={() => scrollToSection(evidenceSectionRef)} />
                    {getWorkflowIdFromTask(managedTask) ? (
                      <SectionJumpButton label="Workflow" onClick={() => scrollToSection(workflowSectionRef)} />
                    ) : null}
                    <SectionJumpButton label="Audit Trail" onClick={() => scrollToSection(auditSectionRef)} />
                  </div>

                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
                    <button
                      className="btn btn-ghost btn-xs"
                      type="button"
                      onClick={() => {
                        handleCloseManageTask();
                        handleOpenFixPlan(
                          {
                            ...managedTask,
                            framework: managedTask.framework || framework,
                            reason_summary: managedTask.description,
                            state: managedTask.status,
                          },
                          managedTask.source || "task"
                        );
                      }}
                      disabled={remediationTaskUpdating}
                    >
                      Open Fix Plan
                    </button>
                    {canDirectValidateAndClose ? (
                      <button
                        className={validateAndCloseReasonMissing ? "btn btn-ghost btn-xs" : "btn btn-primary btn-xs"}
                        type="button"
                        onClick={handleValidateAndCloseClick}
                        disabled={remediationTaskUpdating}
                        style={closureReasonWarningButtonStyle}
                        title={
                          validateAndCloseReasonMissing
                            ? "Add a closure reason before using Validate & Close."
                            : "Finalize this approved-exception task in one step."
                        }
                      >
                        {remediationTaskUpdating
                          ? "Saving…"
                          : validateAndCloseReasonMissing
                          ? "Closure Reason Required"
                          : "Validate & Close"}
                      </button>
                    ) : null}
                    {!isClosedTask(managedTask) ? (
                      <button
                        className="btn btn-primary btn-xs"
                        type="button"
                        onClick={handleSaveManagedTask}
                        disabled={saveTaskDisabled}
                        title={
                          closureReasonMissing
                            ? "Closure reason is required before closing this task."
                            : closureStatusBlocked
                            ? "Save the task as Validated before closing it, or use Validate & Close for approved exceptions."
                            : undefined
                        }
                      >
                        {remediationTaskUpdating
                          ? "Saving…"
                          : closureReasonMissing
                          ? "Closure Reason Required"
                          : "Save Task"}
                      </button>
                    ) : null}
                  </div>
                </div>

                {managedTaskInvestigationDraft && taskNeedsCredentialInvestigation(managedTask) ? (
                  <div
                    style={{
                      border: "1px solid color-mix(in srgb, var(--accent), transparent 72%)",
                      borderRadius: 16,
                      padding: 14,
                      display: "grid",
                      gap: 12,
                      background: "color-mix(in srgb, var(--accent), transparent 94%)",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                        gap: 12,
                        flexWrap: "wrap",
                      }}
                    >
                      <div>
                        <div
                          style={{
                            fontSize: 12,
                            fontWeight: 900,
                            textTransform: "uppercase",
                            letterSpacing: ".06em",
                            color: "var(--muted)",
                          }}
                        >
                          Investigation Notes
                        </div>
                        <div style={{ color: "var(--muted)", fontSize: 13, lineHeight: 1.45, marginTop: 4 }}>
                          Capture dependency proof before rotating or deleting active IAM keys. These notes are saved to the task audit trail.
                        </div>
                      </div>

                      <span
                        className="task-severity"
                        style={{
                          color: hasInvestigationContent(getInvestigationMetadata(managedTask)) ? "var(--ok)" : "var(--warn)",
                          background: hasInvestigationContent(getInvestigationMetadata(managedTask))
                            ? "color-mix(in srgb, var(--ok), transparent 90%)"
                            : "color-mix(in srgb, var(--warn), transparent 90%)",
                          border: hasInvestigationContent(getInvestigationMetadata(managedTask))
                            ? "1px solid color-mix(in srgb, var(--ok), transparent 70%)"
                            : "1px solid color-mix(in srgb, var(--warn), transparent 70%)",
                        }}
                      >
                        {getInvestigationStatusLabel(managedTaskInvestigationDraft.investigation_status)}
                      </span>
                    </div>

                    <label style={{ display: "grid", gap: 6, fontSize: 12, color: "var(--muted)", fontWeight: 800 }}>
                      Investigation Status
                      <select
                        value={managedTaskInvestigationDraft.investigation_status}
                        onChange={(event) =>
                          setManagedTaskInvestigationDraft((current) => ({
                            ...(current || {}),
                            investigation_status: event.target.value,
                          }))
                        }
                        disabled={isClosedTask(managedTask)}
                        style={{
                          border: "1px solid color-mix(in srgb, var(--text), transparent 82%)",
                          borderRadius: 12,
                          background: "rgba(15, 23, 42, 0.72)",
                          color: "var(--text)",
                          padding: "10px 12px",
                        }}
                      >
                        <option value="not_started">Not Started</option>
                        <option value="investigating">Investigating</option>
                        <option value="dependency_identified">Dependency Identified</option>
                        <option value="rotation_planned">Rotation Planned</option>
                        <option value="replacement_testing">Replacement Testing</option>
                        <option value="monitoring">Monitoring</option>
                        <option value="rotation_complete">Rotation Complete</option>
                        <option value="ready_for_validation">Ready For Validation</option>
                      </select>
                    </label>

                    <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 10 }}>
                      <InvestigationInput
                        label="Dependency / Workload Owner"
                        value={managedTaskInvestigationDraft.dependency_owner}
                        placeholder="Team, person, vendor, or service owner"
                        disabled={isClosedTask(managedTask)}
                        onChange={(value) =>
                          setManagedTaskInvestigationDraft((current) => ({ ...(current || {}), dependency_owner: value }))
                        }
                      />
                      <InvestigationInput
                        label="Workload / Automation"
                        value={managedTaskInvestigationDraft.workload_name}
                        placeholder="Job, app, script, worker, or automation name"
                        disabled={isClosedTask(managedTask)}
                        onChange={(value) =>
                          setManagedTaskInvestigationDraft((current) => ({ ...(current || {}), workload_name: value }))
                        }
                      />
                      <InvestigationInput
                        label="Secret Location"
                        value={managedTaskInvestigationDraft.secret_location}
                        placeholder="Secrets Manager, .env, GitHub secret, CI/CD variable, etc."
                        disabled={isClosedTask(managedTask)}
                        onChange={(value) =>
                          setManagedTaskInvestigationDraft((current) => ({ ...(current || {}), secret_location: value }))
                        }
                      />
                      <InvestigationInput
                        label="Credential Location"
                        value={managedTaskInvestigationDraft.credential_location}
                        placeholder="Host, repo, workstation, container, or storage path"
                        disabled={isClosedTask(managedTask)}
                        onChange={(value) =>
                          setManagedTaskInvestigationDraft((current) => ({ ...(current || {}), credential_location: value }))
                        }
                      />
                    </div>

                    <InvestigationTextArea
                      label="Last-Used Interpretation"
                      value={managedTaskInvestigationDraft.last_used_interpretation}
                      placeholder="Example: STS usage in us-east-1 indicates this key likely assumes a role for S3 upload automation."
                      disabled={isClosedTask(managedTask)}
                      onChange={(value) =>
                        setManagedTaskInvestigationDraft((current) => ({ ...(current || {}), last_used_interpretation: value }))
                      }
                    />

                    <InvestigationTextArea
                      label="Rotation Plan"
                      value={managedTaskInvestigationDraft.rotation_plan}
                      placeholder="Create replacement key, update secret, test workload, deactivate old key, monitor, then delete."
                      disabled={isClosedTask(managedTask)}
                      onChange={(value) =>
                        setManagedTaskInvestigationDraft((current) => ({ ...(current || {}), rotation_plan: value }))
                      }
                    />

                    <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 10 }}>
                      <label style={{ display: "flex", gap: 8, alignItems: "center", color: "var(--text)", fontSize: 13, fontWeight: 800 }}>
                        <input
                          type="checkbox"
                          checked={Boolean(managedTaskInvestigationDraft.replacement_key_created)}
                          disabled={isClosedTask(managedTask)}
                          onChange={(event) =>
                            setManagedTaskInvestigationDraft((current) => ({
                              ...(current || {}),
                              replacement_key_created: event.target.checked,
                            }))
                          }
                        />
                        Replacement key created
                      </label>
                      <label style={{ display: "flex", gap: 8, alignItems: "center", color: "var(--text)", fontSize: 13, fontWeight: 800 }}>
                        <input
                          type="checkbox"
                          checked={Boolean(managedTaskInvestigationDraft.workload_updated)}
                          disabled={isClosedTask(managedTask)}
                          onChange={(event) =>
                            setManagedTaskInvestigationDraft((current) => ({
                              ...(current || {}),
                              workload_updated: event.target.checked,
                            }))
                          }
                        />
                        Workload / secret updated
                      </label>
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 10 }}>
                      <label style={{ display: "grid", gap: 6, fontSize: 12, color: "var(--muted)", fontWeight: 800 }}>
                        Replacement Key Slot
                        <select
                          value={managedTaskInvestigationDraft.replacement_key_slot || ""}
                          onChange={(event) =>
                            setManagedTaskInvestigationDraft((current) => ({
                              ...(current || {}),
                              replacement_key_slot: event.target.value,
                            }))
                          }
                          disabled={isClosedTask(managedTask)}
                          style={{
                            border: "1px solid color-mix(in srgb, var(--text), transparent 82%)",
                            borderRadius: 12,
                            background: "rgba(15, 23, 42, 0.72)",
                            color: "var(--text)",
                            padding: "10px 12px",
                          }}
                        >
                          <option value="">Not recorded</option>
                          <option value="key1">Access key 1</option>
                          <option value="key2">Access key 2</option>
                        </select>
                      </label>

                      <InvestigationInput
                        label="Replacement Key Created / Rotated At"
                        value={managedTaskInvestigationDraft.replacement_key_created_at}
                        placeholder="Example: 2026-05-03T22:10:00Z"
                        disabled={isClosedTask(managedTask)}
                        onChange={(value) =>
                          setManagedTaskInvestigationDraft((current) => ({ ...(current || {}), replacement_key_created_at: value }))
                        }
                      />

                      <InvestigationInput
                        label="Replacement Key ID Hint"
                        value={managedTaskInvestigationDraft.replacement_key_id_hint}
                        placeholder="Optional: key ID prefix or last 4–6 chars only; never store the secret"
                        disabled={isClosedTask(managedTask)}
                        onChange={(value) =>
                          setManagedTaskInvestigationDraft((current) => ({ ...(current || {}), replacement_key_id_hint: value }))
                        }
                      />

                      <label style={{ display: "grid", gap: 6, fontSize: 12, color: "var(--muted)", fontWeight: 800 }}>
                        Old Key Retirement Action
                        <select
                          value={managedTaskInvestigationDraft.old_key_retirement_action || ""}
                          onChange={(event) =>
                            setManagedTaskInvestigationDraft((current) => ({
                              ...(current || {}),
                              old_key_retirement_action: event.target.value,
                            }))
                          }
                          disabled={isClosedTask(managedTask)}
                          style={{
                            border: "1px solid color-mix(in srgb, var(--text), transparent 82%)",
                            borderRadius: 12,
                            background: "rgba(15, 23, 42, 0.72)",
                            color: "var(--text)",
                            padding: "10px 12px",
                          }}
                        >
                          <option value="">Not recorded</option>
                          <option value="pending_rotation">Pending rotation</option>
                          <option value="deactivated">Deactivated</option>
                          <option value="deleted">Deleted</option>
                          <option value="rotated">Rotated in place</option>
                          <option value="not_required">Replacement not required</option>
                        </select>
                      </label>
                    </div>

                    <InvestigationTextArea
                      label="Test Result"
                      value={managedTaskInvestigationDraft.test_result}
                      placeholder="What was tested and what passed/failed?"
                      disabled={isClosedTask(managedTask)}
                      onChange={(value) =>
                        setManagedTaskInvestigationDraft((current) => ({ ...(current || {}), test_result: value }))
                      }
                    />

                    <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 10 }}>
                      <InvestigationInput
                        label="Monitoring Window"
                        value={managedTaskInvestigationDraft.monitoring_window}
                        placeholder="Example: Monitor CloudTrail / app logs for 24 hours"
                        disabled={isClosedTask(managedTask)}
                        onChange={(value) =>
                          setManagedTaskInvestigationDraft((current) => ({ ...(current || {}), monitoring_window: value }))
                        }
                      />
                      <InvestigationInput
                        label="Monitoring Result"
                        value={managedTaskInvestigationDraft.monitoring_result}
                        placeholder="Example: No failed auth after deactivation"
                        disabled={isClosedTask(managedTask)}
                        onChange={(value) =>
                          setManagedTaskInvestigationDraft((current) => ({ ...(current || {}), monitoring_result: value }))
                        }
                      />
                    </div>

                    <InvestigationTextArea
                      label="Ready for Validation Reason"
                      value={managedTaskInvestigationDraft.ready_for_validation_reason}
                      placeholder="Why is this task safe to validate now?"
                      disabled={isClosedTask(managedTask)}
                      onChange={(value) =>
                        setManagedTaskInvestigationDraft((current) => ({ ...(current || {}), ready_for_validation_reason: value }))
                      }
                    />

                    <InvestigationTextArea
                      label="Evidence Links"
                      value={managedTaskInvestigationDraft.evidence_links}
                      placeholder="One link, file path, ticket, or reference per line"
                      rows={2}
                      disabled={isClosedTask(managedTask)}
                      onChange={(value) =>
                        setManagedTaskInvestigationDraft((current) => ({ ...(current || {}), evidence_links: value }))
                      }
                    />

                    <InvestigationTextArea
                      label="Notes"
                      value={managedTaskInvestigationDraft.notes}
                      placeholder="Additional investigation notes for the audit trail."
                      disabled={isClosedTask(managedTask)}
                      onChange={(value) =>
                        setManagedTaskInvestigationDraft((current) => ({ ...(current || {}), notes: value }))
                      }
                    />

                    {!isClosedTask(managedTask) && getInvestigationReadinessIssues(managedTask, managedTaskInvestigationDraft).length ? (
                      <div
                        style={{
                          border: "1px solid color-mix(in srgb, var(--warn), transparent 62%)",
                          borderRadius: 14,
                          background: "color-mix(in srgb, var(--warn), transparent 91%)",
                          color: "var(--warn)",
                          padding: "10px 12px",
                          fontSize: 12,
                          lineHeight: 1.45,
                        }}
                      >
                        <strong>Ready-for-validation requirements</strong>
                        <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
                          {getInvestigationReadinessIssues(managedTask, managedTaskInvestigationDraft).map((issue) => (
                            <li key={issue}>{issue}</li>
                          ))}
                        </ul>
                      </div>
                    ) : null}

                    {!isClosedTask(managedTask) ? (
                      <div style={{ display: "flex", justifyContent: "flex-end" }}>
                        <button
                          className="btn btn-ghost btn-xs"
                          type="button"
                          onClick={handleSaveManagedTaskInvestigation}
                          disabled={managedTaskInvestigationSaving || remediationTaskUpdating}
                        >
                          {managedTaskInvestigationSaving ? "Saving Investigation…" : "Save Investigation Notes"}
                        </button>
                      </div>
                    ) : null}

                    {!isClosedTask(managedTask) &&
                    canShowReadyForValidationNextStep(managedTask, managedTaskInvestigationDraft) ? (
                      <div
                        style={{
                          border: "1px solid color-mix(in srgb, var(--ok), transparent 62%)",
                          borderRadius: 14,
                          background: "color-mix(in srgb, var(--ok), transparent 91%)",
                          padding: "12px 14px",
                          display: "grid",
                          gap: 10,
                        }}
                      >
                        <div style={{ color: "var(--ok)", fontWeight: 900 }}>
                          Rotation evidence complete.
                        </div>
                        <div style={{ color: "var(--text)", fontSize: 13, lineHeight: 1.55 }}>
                          Next step: mark this task Ready for Validation so OASYS can verify the old key is retired in the latest IAM credential report.
                        </div>
                        <div style={{ display: "flex", justifyContent: "flex-end" }}>
                          <button
                            className="btn btn-primary btn-xs"
                            type="button"
                            onClick={() => handleMarkTaskReadyForValidation(managedTask)}
                            disabled={remediationTaskUpdating}
                          >
                            Mark Ready for Validation
                          </button>
                        </div>
                      </div>
                    ) : null}

                    {!isClosedTask(managedTask) &&
                    getValidationPendingGuidance(managedTask, managedTaskInvestigationDraft) ? (
                      <div
                        style={{
                          border: "1px solid color-mix(in srgb, var(--warn), transparent 66%)",
                          borderRadius: 14,
                          background: "color-mix(in srgb, var(--warn), transparent 93%)",
                          color: "var(--warn)",
                          padding: "10px 12px",
                          fontSize: 12,
                          lineHeight: 1.45,
                        }}
                      >
                        {getValidationPendingGuidance(managedTask, managedTaskInvestigationDraft)}
                      </div>
                    ) : null}
                  </div>
                ) : null}

                <div ref={closureSectionRef} style={{ scrollMarginTop: 18, display: "grid", gap: 12 }}>
                  <ValidationClosureGuardPanel
                    task={managedTask}
                    closureGuard={managedTaskClosureGuard}
                    loading={managedTaskClosureGuardLoading}
                    error={managedTaskClosureGuardError}
                    onRefresh={handleLoadManagedTaskClosureGuard}
                    onRunValidation={handleRunManagedTaskValidation}
                    onOpenValidationDashboard={handleOpenValidationDashboard}
                    validationActionLoading={managedTaskValidationActionRunning}
                    validationActionError={managedTaskValidationActionError}
                  />
                  <ClosureReadinessPanel task={managedTask} evidenceRows={evidenceRows} />
                </div>

                <div ref={validationSectionRef} style={{ scrollMarginTop: 18 }}>
                  <ValidationHistoryPanel
                    task={managedTask}
                    validationHistory={validationRows}
                    loading={managedTaskValidationHistoryLoading}
                    error={managedTaskValidationHistoryError}
                    onRefresh={handleLoadManagedTaskValidationHistory}
                  />
                </div>

                <div ref={evidenceSectionRef} style={{ scrollMarginTop: 18 }}>
                  <EvidencePanel
                    task={managedTask}
                    evidence={evidenceRows}
                    loading={managedTaskEvidenceLoading}
                    error={managedTaskEvidenceError}
                    onRefresh={handleLoadManagedTaskEvidence}
                    onCreateEvidence={handleCreateManagedTaskEvidence}
                    creating={managedTaskEvidenceCreating}
                    createError={managedTaskEvidenceCreateError}
                  />
                </div>

                <RecoveryLifecycleTimeline
                  task={managedTask}
                  events={managedTaskEvents}
                  loading={managedTaskEventsLoading}
                />

                <div
                  ref={auditSectionRef}
                  style={{
                    border: "1px solid color-mix(in srgb, var(--text), transparent 86%)",
                    borderRadius: 16,
                    padding: 14,
                    display: "grid",
                    gap: 10,
                    scrollMarginTop: 18,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 10,
                    }}
                  >
                    <div
                      style={{
                        fontSize: 12,
                        fontWeight: 900,
                        textTransform: "uppercase",
                        letterSpacing: ".06em",
                        color: "var(--muted)",
                      }}
                    >
                      Audit Trail
                    </div>

                    <button
                      className="btn btn-ghost btn-xs"
                      type="button"
                      onClick={() => handleLoadManagedTaskEvents(managedTask.id)}
                      disabled={managedTaskEventsLoading}
                    >
                      {managedTaskEventsLoading ? "Refreshing…" : "Refresh"}
                    </button>
                  </div>

                  {managedTaskEventsError ? (
                    <div
                      style={{
                        color: "var(--danger)",
                        fontSize: 12,
                        lineHeight: 1.45,
                      }}
                    >
                      {managedTaskEventsError}
                    </div>
                  ) : null}

                  {managedTaskEventsLoading && managedTaskEvents.length === 0 ? (
                    <div style={{ color: "var(--muted)", fontSize: 13 }}>
                      Loading task audit trail…
                    </div>
                  ) : null}

                  {!managedTaskEventsLoading && managedTaskEvents.length === 0 && !managedTaskEventsError ? (
                    <div style={{ color: "var(--muted)", fontSize: 13 }}>
                      No audit events recorded yet. New task changes will appear here.
                    </div>
                  ) : null}

                  {managedTaskEvents.length > 0 ? (
                    <div style={{ display: "grid", gap: 8 }}>
                      {managedTaskEvents.map((event) => {
                        const color = getEventToneColor(event.event_type);
                        const fromState = cleanValue(event.from_state, "");
                        const toState = cleanValue(event.to_state, "");
                        const transition = fromState && toState ? formatAuditDisplayText(formatAuditTransition(fromState, toState)) : "";
                        const eventMetadata = event.event_metadata || {};
                        const auditChangeLabel =
                          cleanValue(eventMetadata.audit_change_label, "") ||
                          cleanValue(eventMetadata.auditChangeLabel, "") ||
                          (normalizeText(event.event_type) === "copilot_draft_applied" && eventMetadata.pathway_name
                            ? `${eventMetadata.pathway_name} applied`
                            : "");
                        const displayedChange = formatAuditDisplayText(auditChangeLabel || transition);
                        const displayedEventLabel = formatAuditDisplayText(getEventLabel(event.event_type));
                        const displayedEventDetail = formatAuditDisplayText(getEventDetail(event));

                        return (
                          <div
                            key={event.id}
                            className="open-item-pill"
                            style={{
                              cursor: "default",
                              alignItems: "stretch",
                              borderLeft: `3px solid ${color}`,
                              display: "grid",
                              gap: 5,
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                gap: 10,
                                alignItems: "center",
                                flexWrap: "wrap",
                              }}
                            >
                              <div style={{ fontWeight: 900, fontSize: 13 }}>
                                {displayedEventLabel}
                              </div>
                              <div style={{ color: "var(--muted)", fontSize: 12 }}>
                                {formatTimestamp(event.created_at)}
                              </div>
                            </div>

                            <div
                              style={{
                                color: "var(--muted)",
                                fontSize: 12,
                                display: "flex",
                                gap: 8,
                                flexWrap: "wrap",
                                lineHeight: 1.35,
                              }}
                            >
                              <span>Actor: {event.actor_name || event.actor_user_id || "System"}</span>
                              {displayedChange ? <span>Change: {displayedChange}</span> : null}
                            </div>

                            <div style={{ color: "var(--text)", fontSize: 12, lineHeight: 1.45 }}>
                              {displayedEventDetail}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : null}
                </div>

                <div
                  style={{
                    border: "1px solid color-mix(in srgb, var(--text), transparent 86%)",
                    borderRadius: 16,
                    padding: 14,
                    display: "grid",
                    gap: 8,
                  }}
                >
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 900,
                      textTransform: "uppercase",
                      letterSpacing: ".06em",
                      color: "var(--muted)",
                    }}
                  >
                    Workflow guardrails
                  </div>
                  <div style={{ color: "var(--muted)", fontSize: 13, lineHeight: 1.55 }}>
                    {isClosedTask(managedTask)
                      ? "This task is closed and locked. Reopening should be handled through a deliberate future workflow so the audit trail remains reliable."
                      : approvedExceptionAccepted
                      ? "Approved-exception tasks can be finalized with Validate & Close after evidence is attached and a closure reason is recorded. Standard remediation tasks still require Validated status before closure."
                      : "Tasks can only be closed after evidence is attached, validation is accepted, task status is finalized as Validated, and a closure reason is recorded. The backend also enforces these rules."}
                  </div>
                </div>

                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
                  <button
                    className="btn btn-ghost btn-xs"
                    type="button"
                    onClick={() => {
                      handleCloseManageTask();
                      handleOpenFixPlan(
                        {
                          ...managedTask,
                          framework: managedTask.framework || framework,
                          reason_summary: managedTask.description,
                          state: managedTask.status,
                        },
                        managedTask.source || "task"
                      );
                    }}
                    disabled={remediationTaskUpdating}
                  >
                    Open Fix Plan
                  </button>

                  <button
                    className="btn btn-ghost btn-xs"
                    type="button"
                    onClick={handleCloseManageTask}
                    disabled={remediationTaskUpdating}
                  >
                    {isClosedTask(managedTask) ? "Close" : "Cancel"}
                  </button>

                  {canDirectValidateAndClose ? (
                    <button
                      className={validateAndCloseReasonMissing ? "btn btn-ghost btn-xs" : "btn btn-primary btn-xs"}
                      type="button"
                      onClick={handleValidateAndCloseClick}
                      disabled={remediationTaskUpdating}
                      style={closureReasonWarningButtonStyle}
                      title={
                        validateAndCloseReasonMissing
                          ? "Add a closure reason before using Validate & Close."
                          : "Finalize this approved-exception task in one step."
                      }
                    >
                      {remediationTaskUpdating
                        ? "Saving…"
                        : validateAndCloseReasonMissing
                        ? "Closure Reason Required"
                        : "Validate & Close"}
                    </button>
                  ) : null}

                  {!isClosedTask(managedTask) ? (
                    <button
                      className="btn btn-ghost btn-xs"
                      type="button"
                      onClick={handleSaveManagedTask}
                      disabled={saveTaskDisabled}
                      title={
                        closureReasonMissing
                          ? "Closure reason is required before closing this task."
                          : closureStatusBlocked
                          ? "Save the task as Validated before closing it, or use Validate & Close for approved exceptions."
                          : undefined
                      }
                    >
                      {remediationTaskUpdating
                        ? "Saving…"
                        : closureReasonMissing
                        ? "Closure Reason Required"
                        : "Save Task"}
                    </button>
                  ) : null}
                </div>
              </div>
            </section>
          </div>
  );
}
